/**
 * App.js
 */

import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import GameBoard from './components/GameBoard';
import NameThatGuyGame from './components/NameThatGuyGame';
import DigitsGame from './components/DigitsGame';
import InviteBanner from './components/InviteBanner.js';
import AuthModal from './components/AuthModal';
import UsernamePickerModal from './components/UsernamePickerModal';
import SubmitSrModal from './components/SubmitSrModal';
import { PartnerBanner } from './components/PartnerPromo';
import Leaderboard from './components/Leaderboard';
import Profile from './components/Profile';
import InstallPrompt from './components/InstallPrompt';
import GameX from './components/GameX';
import NextGameNudge from './components/NextGameNudge';
import { signOut } from './utils/auth';
import { supabase, supabaseDataFetchHasFired } from './utils/supabase';
import { readAttribution, isAttributionSynced, markAttributionSynced } from './utils/attribution';
import { withTimeout, loadWithRetry, fetchWithAuthRetry } from './utils/withTimeout';
import './App.css';
import { BrowserRouter, Routes, Route, useParams } from 'react-router-dom';
import ResetPassword from './components/ResetPassword';
import { loadPlayers, loadColleges, loadNBAPlayers, loadNBAColleges, loadMoshPlayers, loadMoshColleges } from './data/parseCSV';
import { createGame, joinGame, createDailyChallenge, createNBADailyChallenge, createMoshDailyChallenge, TOTAL_QUESTIONS } from './utils/gameEngine';
import { getDailyData, hasPlayedToday, getDailyProgress, getNBADailyData, hasPlayedNBAToday, getNBADailyProgress, getMoshDailyData, hasPlayedMoshToday, getMoshDailyProgress, saveMoshDailyResult, clearMoshDailyProgress, saveNBADailyResult, clearNBADailyProgress, claimGuestDailyResult, claimGuestNBADailyResult, claimGuestMoshDailyResult, fetchTodaysNTGQuestion, hasPlayedNTGToday, getNTGDailyData, claimGuestNTGDailyResult, fetchTodaysNTGSrPuzzle, hasPlayedNTGSrToday, getNTGSrDailyData, claimGuestNTGSrDailyResult, fetchTodaysDigitsQuestion, hasPlayedDigitsToday, getDigitsDailyData } from './utils/dailyChallenge';
import PrivacyPolicy from './components/PrivacyPolicyPage';
import { ntgJrShareText, ntgSrShareText } from './utils/ntgShare';
import { Analytics } from '@vercel/analytics/react';

const SCREENS = {
  LOADING: 'loading',
  HOME: 'home',
  MODE_SELECT: 'mode_select',
  SPORT_SELECT: 'sport_select',
  CREATE_MODE: 'create_mode',
  PLAYING: 'playing',
  NFL_BEGIN: 'nfl_begin',
  NTG_BEGIN: 'ntg_begin',
  NTG_PLAYING: 'ntg_playing',
  DIGITS_BEGIN: 'digits_begin',
  DIGITS_PLAYING: 'digits_playing',
  NTG_SR_BEGIN: 'ntg_sr_begin',
  NTG_SR_PLAYING: 'ntg_sr_playing',
  DAILY_LOCKED: 'daily_locked',
  EXPIRED: 'expired',
  LOAD_ERROR: 'load_error',
};

// Reusable home-screen notice banner. To show a notice: set to true and update the
// banner copy in the HOME render block. Pacific transition banner retired 2026-07-09.
// The Sr.-submit pitch ran here until 2026-08-30, when the slot went to the PocketBookie
// partnership. Matt flipped the two spots on 2026-09-16: Sr.-submit is back up top and
// the PocketBookie banner moved down to the home footer, under the feedback line (see
// PartnerPromo.jsx). Sr.-submit CTAs also still live on both GameX result cards.
const SHOW_UPDATE_BANNER = true;

// How new a profile row must be for a tagged link to attribute to it. Keeps an
// existing user who clicks an ad from being credited as a campaign signup.
const NEW_ACCOUNT_WINDOW_MS = 60 * 60 * 1000;

// Screens where a resume-reload destroys nothing in progress. Anything NOT listed
// here (PLAYING, NTG_PLAYING, and the MODE/SPORT/CREATE setup screens) is left
// alone so a glance at another app for 45s never costs someone their game. This is
// an allowlist on purpose: any screen added later defaults to "do not reload".
const RELOAD_SAFE_SCREENS = new Set([
  SCREENS.HOME,
  SCREENS.LOADING,
  SCREENS.NFL_BEGIN,   // pre-BEGIN splash: nothing in progress -> safe to reload (deep-link wedge recovery)
  SCREENS.NTG_BEGIN,   // pre-BEGIN splash: nothing in progress -> safe to reload
  SCREENS.DIGITS_BEGIN, // pre-BEGIN splash: nothing in progress -> safe to reload
  SCREENS.NTG_SR_BEGIN, // pre-BEGIN splash: nothing in progress -> safe to reload (/sr ad traffic)
  SCREENS.DAILY_LOCKED,
  SCREENS.EXPIRED,
  SCREENS.LOAD_ERROR,
]);

const COACH_QUOTES = [
{ quote: "Winning is not a sometime thing; it's an all the time thing.", coach: "Vince Lombardi" },
{ quote: "The only place success comes before work is in the dictionary.", coach: "Vince Lombardi" },
{ quote: "Nobody who ever gave his best regretted it.", coach: "George Halas" },
{ quote: "Leadership is a matter of having people look at you and gain confidence.", coach: "Tom Landry" },
{ quote: "You fail all the time. But you aren't a failure until you start blaming someone else.", coach: "Bum Phillips" },
{ quote: "One man practicing sportsmanship is far better than a hundred teaching it.", coach: "Knute Rockne" },
{ quote: "We didn't lose the game; we just ran out of time.", coach: "Vince Lombardi" },
{ quote: "If you want to win, do the ordinary things better than anyone else does them.", coach: "Chuck Noll" },
{ quote: "The man who complains about the way the ball bounces is likely the one who dropped it.", coach: "Lou Holtz" },
{ quote: "If you're not getting better, you're getting worse.", coach: "Pat Riley" },
{ quote: "I don't think anything is unrealistic if you believe you can do it.", coach: "Mike Ditka" },
{ quote: "The difference between a successful person and others is not a lack of strength, but a lack of will.", coach: "Vince Lombardi" },
{ quote: "If you aren't going all the way, why go at all?", coach: "Joe Namath" },
{ quote: "Adversity is the state in which man mostly easily becomes acquainted with himself.", coach: "John Madden" },
{ quote: "Most people give up just when they're about to achieve success.", coach: "Don Shula" },
{ quote: "Next question.", coach: "Bill Belichick" },
{ quote: "We're on to Cincinnati.", coach: "Bill Belichick" },
{ quote: "Do your job.", coach: "Bill Belichick" },
{ quote: "You're killing me, Smalls.", coach: "Hamilton \"Ham\" Porter (The Sandlot)" },
{ quote: "You gotta shake it, before you bake it!", coach: "Cal Naughton Jr. (Talladega Nights)" },
{ quote: "If you're not first, you're last.", coach: "Reese Bobby (Talladega Nights)" },
{ quote: "I drive a Dodge Stratus!", coach: "Will Ferrell" },
{ quote: "You can do it!", coach: "Rob Schneider" },
{ quote: "I'm kind of a big deal.", coach: "Ron Burgundy (Anchorman)" },
{ quote: "60% of the time, it works every time.", coach: "Brian Fantana (Anchorman)" },
{ quote: "We're going streaking!", coach: "Frank the Tank (Old School)" },
{ quote: "I'm not superstitious, but I am a little stitious.", coach: "Michael Scott (The Office)" },
{ quote: "It's not about the weight, people. It's about the winners and the losers!", coach: "Tony Perkis (Heavyweights)" },
{ quote: "Feel the burn! No pain, no gain! Shut up and train!", coach: "Tony Perkis (Heavyweights)" },
{ quote: "Nobody makes me bleed my own blood. Nobody.", coach: "White Goodman (Dodgeball)" },
{ quote: "No ragrets", coach: "Scotty P's Ink (We're the Millers)" },
{ quote: "We talking about practice. Not a game. Practice.", coach: "Allen Iverson" },
{ quote: "You play. To win. The game. Hello?!", coach: "Herm Edwards" },
{ quote: "Sometimes you eat the bar. Sometimes the bar eats you.", coach: "The Stranger" },
{ quote: "Crab cakes and football—that's what Maryland does!", coach: "Cousin Flip (Wedding Crashers)" },
{ quote: "I feel like I'm taking crazy pills!", coach: "Mugatu (Zoolander)" },
{ quote: "I am speed.", coach: "Lightning McQueen" },
{ quote: "You can't handle the truth!", coach: "Colonel Jessup (A Few Good Men)" },
{ quote: "I'm just here so I won't get fined.", coach: "Marshawn Lynch" },
{ quote: "Dong, where is my automobile?", coach: "Grandpa Fred (Sixteen Candles)" },
{ quote: "That escalated quickly.", coach: "Ron Burgundy (Anchorman)" },
{ quote: "ELE! Everybody love everybody!", coach: "Jackie Moon (Semi-Pro)" },
{ quote: "If you don't like that, you don't like Flint Tropics basketball.", coach: "Jackie Moon (Semi-Pro)" },
{ quote: "We're gonna play hard, we're gonna play fast, and we're gonna look good doing it.", coach: "Jackie Moon (Semi-Pro)" },
{ quote: "What is this?! A school for ants?!", coach: "Derek Zoolander" },
{ quote: "I live in a van down by the river… and even I wouldn't run that play.", coach: "Matt Foley" },
{ quote: "If you can dodge a wrench, you can dodge a ball.", coach: "Patches O'Houlihan (Dodgeball)" },
{ quote: "That's a bold strategy, Cotton. Let's see if it pays off for 'em.", coach: "Pepper Brooks (Dodgeball)" },
{ quote: "Necessary? Is it necessary for me to drink my own urine?", coach: "Patches O'Houlihan (Dodgeball)" },
{ quote: "You gotta keep livin', man. L-I-V-I-N.", coach: "Trent Walker (Swingers)" },
{ quote: "Baby, you're so money and you don't even know it.", coach: "Trent Walker (Swingers)" },
{ quote: "This place is dead anyway.", coach: "Trent Walker (Swingers)" },
{ quote: "You're like a big bear with claws… with these claws!", coach: "Trent Walker (Swingers)" },
{ quote: "Why would I want to do the dishes?", coach: "Gary Grobowski (The Break-Up)" },
{ quote: "You gotta believe in something.", coach: "Fred Claus (Fred Claus)" },
{ quote: "Clear eyes, full hearts, can't lose.", coach: "Coach Taylor (Friday Night Lights)" },
{ quote: "I'm gonna make him an offer he can't refuse.", coach: "Vito Corleone (The Godfather)" },
{ quote: "I am serious. And don't call me Shirley.", coach: "Dr. Rumack (Airplane!)" },
{ quote: "Stay classy, San Diego.", coach: "Ron Burgundy (Anchorman)" },
{ quote: "The painting was a gift, Todd. I'm taking it with me.", coach: "Jeremy Grey (Wedding Crashers)" },
{ quote: "You're tacky and I hate you.", coach: "Fancy Pants (School of Rock)" },
{ quote: "Those who can't do, teach. And those who can't teach, teach gym.", coach: "Dewey Finn (School of Rock)" },
{ quote: "I have been touched by your kids... and I'm pretty sure that I've touched them.", coach: "Dewey Finn (School of Rock)" },
{ quote: "Math is a wonderful thing. Math is a really cool thing.", coach: "Dewey Finn (School of Rock)" },
{ quote: "Fat guy in a little coat.", coach: "Tommy Callahan (Tommy Boy)" },
{ quote: "You can get a good look at a T-bone by sticking your head up a butcher's ass... no, wait. It's gotta be your bull.", coach: "Tommy Callahan (Tommy Boy)" },
{ quote: "Housekeeping! You want me fluff pillow?", coach: "Tommy Callahan (Tommy Boy)" },
{ quote: "Did you eat a lot of paint chips when you were a kid?", coach: "Richard Hayden (Tommy Boy)" },
{ quote: "So you're telling me there's a chance!", coach: "Lloyd Christmas (Dumb and Dumber)" },
{ quote: "We've got no food, we've got no jobs... our pets' heads are fallin' off!", coach: "Harry Dunne (Dumb and Dumber)" },
{ quote: "I expected the Rocky Mountains to be a little rockier than this.", coach: "Harry Dunne (Dumb and Dumber)" },
{ quote: "You're a virgin who can't drive.", coach: "Tai Frasier (Clueless)" },
{ quote: "Is that true? That if you don't use it, you lose it?", coach: "Andy Stitzer (The 40-Year-Old Virgin)" },
{ quote: "Oh, shut up, Seth. We went to temple.", coach: "Seth's Dad (The 40-Year-Old Virgin)" },
{ quote: "You wanna hurt me? Go right ahead if it makes you feel any better.", coach: "Del Griffith (Planes, Trains and Automobiles)" },
{ quote: "I like me. My wife likes me.", coach: "Del Griffith (Planes, Trains and Automobiles)" },
{ quote: "Army training, sir!", coach: "Russell Ziskey (Stripes)" },
{ quote: "Slappin' da bass!", coach: "Sydney Fife (I Love You, Man)" },
{ quote: "I'm a dude playing a dude disguised as another dude.", coach: "Kirk Lazarus (Tropic Thunder)" },
{ quote: "Daddy, I want an Oompa-Loompa!", coach: "Veruca Salt (Willy Wonka & the Chocolate Factory)" },
{ quote: "I hate the fuckin' Eagles, man.", coach: "The Dude (The Big Lebowski)" },
{ quote: "You are a sad, strange little man, and you have my pity. Farewell.", coach: "Buzz Lightyear (Toy Story)" },
{ quote: "I'm Ron Burgundy?", coach: "Ron Burgundy (Anchorman)" },
{ quote: "Hello, this is Ned Schneebly.", coach: "Dewey Finn (School of Rock)" },
{ quote: "I better use my strong hand.", coach: "Hanson (Scary Movie 2)" },
{ quote: "Lead me, follow me, or get the hell out of my way.", coach: "General Patton" },
{ quote: "If everybody is thinking alike, then somebody isn't thinking.", coach: "General Patton" },
{ quote: "Big mistake. Big. Huge.", coach: "Vivian (Pretty Woman)" },
{ quote: "You get your ass out there, and you find that fucking dog!", coach: "Billy Madison (Billy Madison)" },
{ quote: "You blew it!", coach: "Billy Madison (Billy Madison)" },
{ quote: "O'Doyle Rules!", coach: "O'Doyle (Billy Madison)" },
{ quote: "A simple 'wrong' would've done just fine.", coach: "Billy Madison (Billy Madison)" },
{ quote: "Happy Gilmore accomplished that feat no more than an hour ago.", coach: "Mr. Larson (Happy Gilmore)" },
{ quote: "Good news, everybody, we're extending arts and crafts time by four hours today.", coach: "Hal L. (Happy Gilmore)" },
{ quote: "It's all in the hips.", coach: "Chubbs Peterson (Happy Gilmore)" },
{ quote: "...but I wipe my own ass, I wipe my own ass!", coach: "Julian (Big Daddy)" },
{ quote: "Weed is tight, weed is tight.", coach: "Jonah Hill (This is the End)" },
{ quote: "I mean, seriously, how often do you really look at a man's shoes?", coach: "Red (The Shawshank Redemption)" },
{ quote: "Andy crawled to freedom through five hundred yards of shit smelling foulness I can't even imagine, or maybe I just don't want to.", coach: "Red (The Shawshank Redemption)" },
];

function App({ joinMode = false }) {
  const { matchId } = useParams();
  const [screen, setScreen] = useState(SCREENS.LOADING);
  const screenRef = useRef(screen);
  const [players, setPlayers] = useState([]);
  const [nbaPlayers, setNBAPlayers] = useState([]);
  const [nbaColleges, setNBAColleges] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [gameState, setGameState] = useState(null);
  // Remount GameBoard per game. NFL, NBA and Mosh dailies all render on
  // SCREENS.PLAYING, so navigating results -> next daily never unmounted the
  // board and the finished game kept showing (the dead NEXT GAME button,
  // 2026-08-25). gbKey changes per created game; the key prop forces a fresh
  // mount. Monotonic counter, not Date.now(), so two games in the same
  // millisecond still differ.
  const gbKeyRef = useRef(0);
  const setFreshGame = useCallback((state) => {
    gbKeyRef.current += 1;
    setGameState(state ? { ...state, gbKey: gbKeyRef.current } : state);
  }, []);
  const [ntgQuestion, setNtgQuestion] = useState(null);
  const [digitsQuestion, setDigitsQuestion] = useState(null);
  const [digitsCloudStreak, setDigitsCloudStreak] = useState(null);
  const [digitsCloudStrikes, setDigitsCloudStrikes] = useState(0);
  const [ntgSrPuzzle, setNtgSrPuzzle] = useState(null);
  const [joinError, setJoinError] = useState('');
  const [coachQuote] = useState(() => COACH_QUOTES[Math.floor(Math.random() * COACH_QUOTES.length)]);
  const [dailyData, setDailyData] = useState(() => getDailyData());
  const [cloudStreak, setCloudStreak] = useState(null);
  const [nbaCloudStreak, setNBACloudStreak] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [showUsernamePicker, setShowUsernamePicker] = useState(false);
  const [showSrSubmit, setShowSrSubmit] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
 const [showBell, setShowBell] = useState(false);
const bellRef = useRef(null);
const [showInfo, setShowInfo] = useState(false);
const [showRules, setShowRules] = useState(false);
const infoRef = useRef(null);
const leaderboardRef = useRef(null);
const profileRef = useRef(null);
const [lockedSport, setLockedSport] = useState('nfl');
const [moshPlayers, setMoshPlayers] = useState([]);
const [moshColleges, setMoshColleges] = useState([]);
const [moshCloudStreak, setMoshCloudStreak] = useState(null);
const [ironmanCloudStreak, setIronmanCloudStreak] = useState(null);
const [ntgCloudStreak, setNtgCloudStreak] = useState(null);
const [ntgCloudStrikes, setNtgCloudStrikes] = useState(0);
const [ntgSrCloudStreak, setNtgSrCloudStreak] = useState(null);
const [ntgSrCloudStrikes, setNtgSrCloudStrikes] = useState(0);
const [claimedStreaks, setClaimedStreaks] = useState({});
const [h2hSport, setH2hSport] = useState('nfl');
const [seenSdRounds, setSeenSdRounds] = useState({}); // { matchId: lastSeenSdRound }
const [isCreating, setIsCreating] = useState(false);
const [createError, setCreateError] = useState(null);
const [dailyError, setDailyError] = useState(null);

useEffect(() => {
  const handleClickOutside = (e) => {
    if (bellRef.current && !bellRef.current.contains(e.target)) {
      setShowBell(false);
    }
  };
  document.addEventListener('mousedown', handleClickOutside);
  return () => document.removeEventListener('mousedown', handleClickOutside);
}, []);

useEffect(() => {
  const handleClickOutside = (e) => {
    if (infoRef.current && !infoRef.current.contains(e.target)) {
      setShowInfo(false);
    }
  };
  document.addEventListener('mousedown', handleClickOutside);
  return () => document.removeEventListener('mousedown', handleClickOutside);
}, []);

// Reset window scroll to the top on every screen change. Screens swap via React
// state (not navigation), so the browser keeps the prior scroll offset and you
// can land mid-page or at the bottom of a freshly-mounted screen.
useLayoutEffect(() => {
  window.scrollTo(0, 0);
}, [screen]);
  const [joined, setJoined] = useState(false);
  const [showInvite, setShowInvite] = useState(true);

  // All matches (non-finished) — used for GIP panel
  const [gamesInProgress, setGamesInProgress] = useState([]);
  // Finished matches in last 72hrs — used for bell + results
  const [recentFinished, setRecentFinished] = useState([]);
  const [gameOpponents, setGameOpponents] = useState({});
  // Track which finished game IDs have been "seen"
  const [lastBellCheck, setLastBellCheck] = useState(null);
 
  useEffect(() => {
  const handleClickOutside = (e) => {
    // ignore clicks on the nav toggle buttons — they handle their own state
    if (e.target.closest('.top-nav-btn')) return;
    if (leaderboardRef.current && !leaderboardRef.current.contains(e.target)) {
      setShowLeaderboard(false);
    }
    if (profileRef.current && !profileRef.current.contains(e.target)) {
      setShowProfile(false);
    }
  };
  document.addEventListener('mousedown', handleClickOutside);
  return () => document.removeEventListener('mousedown', handleClickOutside);
}, []);

useEffect(() => {
  setShowBell(false);
  setShowInfo(false);
  setShowLeaderboard(false);
  setShowProfile(false);
}, [screen]);

  // ── Load players/colleges ─────────────────────────────────────────
  // Required load: a hang here used to strand the app on LOADING forever.
  // Now each attempt is timeout-bounded and retried; total failure drops to a
  // tappable LOAD_ERROR screen instead of an infinite spinner.
  const loadRosters = useCallback(async () => {
    setScreen(SCREENS.LOADING);
    try {
      const [playerData, collegeData, nbaPlayerData, nbaCollegeData, moshPlayerData, moshCollegeData] =
        await loadWithRetry(() => Promise.all([
          loadPlayers(), loadColleges(),
          loadNBAPlayers(), loadNBAColleges(),
          loadMoshPlayers(), loadMoshColleges(),
        ]));
      setPlayers(playerData);
      setColleges(collegeData);
      setNBAPlayers(nbaPlayerData);
      setNBAColleges(nbaCollegeData);
      setMoshPlayers(moshPlayerData);
      setMoshColleges(moshCollegeData);
      setScreen(joinMode ? SCREENS.LOADING : SCREENS.HOME);
    } catch (err) {
      console.error('Failed to load player data:', err);
      setScreen(SCREENS.LOAD_ERROR);
    }
  }, [joinMode]);

  useEffect(() => { loadRosters(); }, [loadRosters]);



  // ── Load cloud streak ─────────────────────────────────────────────
  // Keyed on the id, NOT the currentUser object: the auth handler mints a fresh
  // object on every auth event, and supabase-js re-emits SIGNED_IN on every
  // tab-return, so an object dep re-ran this boot fetch constantly. The id only
  // changes on a real sign-in/out/switch, which is the only time this should run.
  useEffect(() => {
    const uid = currentUser?.id;
    if (!uid) { setCloudStreak(null); setIronmanCloudStreak(null); setNtgCloudStreak(null); setNtgCloudStrikes(0); setNtgSrCloudStreak(null); setNtgSrCloudStrikes(0); setDigitsCloudStreak(null); setDigitsCloudStrikes(0); return; }
    if (!authReady) return; // wait for the session to settle before the boot fetch
    // maybeSingle so a missing row is null instead of a thrown 406. This is the
    // earliest per-session read, so healing here fixes streaks, the nav name and
    // the profile page in one shot. ensure_profile() is a no-op when the row
    // exists, so the extra call only ever happens for a genuinely orphaned user.
    const readStreaks = () => supabase.from('profiles')
      .select('current_streak, nba_current_streak, mosh_current_streak, ntg_current_streak, ntg_strikes, ntg_sr_current_streak, ntg_sr_strikes, digits_current_streak, digits_strikes, ironman_current_streak')
      .eq('id', uid).maybeSingle();
    readStreaks()
  .then(async ({ data }) => {
    if (!data) {
      await supabase.rpc('ensure_profile');
      ({ data } = await readStreaks());
    }
    if (data) {
      setCloudStreak(data.current_streak);
      setNBACloudStreak(data.nba_current_streak || 0);
      setMoshCloudStreak(data.mosh_current_streak || 0);
      setNtgCloudStreak(data.ntg_current_streak || 0);
      setNtgCloudStrikes(data.ntg_strikes || 0);
      setNtgSrCloudStreak(data.ntg_sr_current_streak || 0);
      setNtgSrCloudStrikes(data.ntg_sr_strikes || 0);
      setDigitsCloudStreak(data.digits_current_streak || 0);
      setDigitsCloudStrikes(data.digits_strikes || 0);
      setIronmanCloudStreak(data.ironman_current_streak || 0);
    }
  })
  .catch(err => console.error('Load cloud streak error:', err));
  }, [currentUser?.id, authReady]);



// ── Auth-settle gate ──────────────────────────────────────────────
// On cold load the boot data calls (leaderboard/profile/cloud-streak) would
// otherwise fire on mount BEFORE supabase-js finishes restoring/refreshing the
// session, so they'd race the in-flight refresh and wedge on "Loading...".
// Await getSession() once at boot, then flip authReady so those calls dispatch
// with a settled token. processLock makes getSession() resolve reliably (no
// deadlock), so this can't hang on a stuck call; signed-out resolves instantly
// (empty session, no network); bounded so a failed settle still falls through.
useEffect(() => {
  let done = false;
  (async () => {
    try { await withTimeout(supabase.auth.getSession(), 8000); } catch {}
    if (!done) setAuthReady(true);
  })();
  return () => { done = true; };
}, []);

// ── Cold-wedge auto-recovery ──────────────────────────────────────
// The residual cold-init wedge leaves the Supabase client stuck BEFORE it issues
// the DATA request, so the page sits on "Loading..." until a manual hard refresh
// (which rebuilds the client). Auto-do that, but ONLY on the wedge's true
// signature: no DATA (/rest/v1/) request dispatched. We key on DATA dispatch, not
// "any fetch" - on a stale-token cold load the AUTH refresh often dispatches even
// when the client is wedged, which previously made this stand down and never fire.
// A slow network DOES dispatch its data request (flag set) and is never reloaded.
// Signed-in only: a guest fires no boot data request, so "nothing dispatched" is
// normal for them - ad traffic is never reloaded.
// Loop-proof: one auto-reload per cold session (sessionStorage), cleared on the
// first successful Supabase response; if still wedged after that one reload, fall
// through to the banner/loading state - never loops, never worse than a manual refresh.
useEffect(() => {
  let hasSession = false;
  try { hasSession = !!localStorage.getItem('sb-hwbuokpowygjecbujtfz-auth-token'); } catch {}
  if (!hasSession) return; // guests dispatch no boot request; never auto-reload ad traffic
  // Deterministic DATA-dispatch probe: a healthy client dispatches this /rest/v1/
  // request (flips the data flag in supabase.js); a wedged client is stuck before
  // the data request leaves, so it never dispatches. Fire-and-forget.
  supabase.from('profiles').select('id').limit(1).then(() => {}, () => {});
  const t = setTimeout(() => {
    if (document.visibilityState !== 'visible') return;       // don't reload a backgrounded tab
    if (supabaseDataFetchHasFired()) return;                  // a DATA request dispatched -> healthy/slow, NOT a wedge (auth-only dispatch no longer counts)
    if (!RELOAD_SAFE_SCREENS.has(screenRef.current)) return;  // never mid-game
    let already = false;
    try { already = !!sessionStorage.getItem('kyg_cold_reload'); } catch {}
    if (already) return;                                      // one reload already -> fall through to banner
    try { sessionStorage.setItem('kyg_cold_reload', '1'); } catch {}
    window.location.reload();                                 // wedge: rebuild client, like a manual refresh
  }, 5000); // measured: healthy data dispatch is ~25ms from mount (valid token) or ~one round-trip (~2.1s on 3G) if a token refresh is needed; wedge never dispatches. 5s leaves ~2.4x margin.
  return () => clearTimeout(t);
}, []);

// ── Auth state change ─────────────────────────────────────────────
useEffect(() => {
  const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
    try {
      if (event === 'SIGNED_OUT' || !session?.user) {
        setCurrentUser(null);
        return;
      }

      const user = session.user;

      // Set user immediately from session for all events - covers INITIAL_SESSION,
      // SIGNED_IN, TOKEN_REFRESHED. Don't wait for profile fetch.
      // supabase-js auto-refreshes the token on tab-return and emits TOKEN_REFRESHED;
      // preserve an already-known username across that churn so it doesn't flip back
      // to the email-prefix. Only fall back to the prefix when we have nothing better.
      setCurrentUser(prev =>
        (prev && prev.id === user.id && prev.username)
          ? { ...user, username: prev.username }
          : { ...user, username: user.email?.split('@')[0] });

      if (event === 'SIGNED_IN') {
        setShowAuth(false);
        const claimUser = { id: user.id, email: user.email };
        Promise.all([
          claimGuestDailyResult(claimUser),
          claimGuestNBADailyResult(claimUser),
          claimGuestMoshDailyResult(claimUser),
          claimGuestNTGDailyResult(claimUser),
          claimGuestNTGSrDailyResult(claimUser),
        ]).then(([nflResult, nbaResult, moshResult, ntgResult, ntgSrResult]) => {
          const claimed = {};
          if (nflResult?.newStreak !== undefined) { setCloudStreak(nflResult.newStreak); claimed.nfl = nflResult.newStreak; }
          if (nbaResult?.newStreak !== undefined) { setNBACloudStreak(nbaResult.newStreak); claimed.nba = nbaResult.newStreak; }
          if (moshResult?.newStreak !== undefined) { setMoshCloudStreak(moshResult.newStreak); claimed.mosh = moshResult.newStreak; }
          if (ntgResult?.newStreak !== undefined) { setNtgCloudStreak(ntgResult.newStreak); claimed.ntg = ntgResult.newStreak; }
          if (ntgResult?.newStrikes !== undefined) setNtgCloudStrikes(ntgResult.newStrikes);
          if (ntgSrResult?.newStreak !== undefined) { setNtgSrCloudStreak(ntgSrResult.newStreak); claimed.ntg_sr = ntgSrResult.newStreak; }
          if (ntgSrResult?.newStrikes !== undefined) setNtgSrCloudStrikes(ntgSrResult.newStrikes);
          if (nflResult?.ironmanStreak !== undefined) setIronmanCloudStreak(nflResult.ironmanStreak);
          if (ntgResult?.ironmanStreak !== undefined) setIronmanCloudStreak(ntgResult.ironmanStreak);
          if (ntgSrResult?.ironmanStreak !== undefined) setIronmanCloudStreak(ntgSrResult.ironmanStreak);
          if (Object.keys(claimed).length > 0) setClaimedStreaks(claimed);
        }).catch(err => console.error('claim guest results error:', err));
      }

      // Defer ALL Supabase data work out of this callback. auth-js AWAITS subscriber
      // callbacks inside its token-refresh critical section, and getSession() (which any
      // data call needs) awaits the init promise the in-flight refresh is holding. So
      // awaiting a data call HERE deadlocks the whole client on a cold expired-token
      // load: the refresh 200s and writes a new token, but no /rest/v1/ ever dispatches
      // and the app wedges (username stranded). setTimeout(0) returns the callback first,
      // letting the refresh/init settle, then we fetch on a live client (retry-wrapped
      // for slow networks). The synchronous setCurrentUser above already set the name.
      setTimeout(async () => {
        try {
          const { data: profile } = await fetchWithAuthRetry(() => supabase
            .from('profiles')
            .select('username, last_bell_check')
            .eq('id', user.id)
            .single());
          // Only ever upgrade the username - never null it. A failed/empty profile fetch
          // must not wipe a name we already have (else the nav falls back to the prefix).
          if (profile?.last_bell_check) setLastBellCheck(new Date(profile.last_bell_check));
          setCurrentUser(prev => ({ ...user, username: profile?.username || prev?.username || user.email?.split('@')[0] }));
          if (event === 'SIGNED_IN' && !profile?.username && session.user.app_metadata?.provider === 'google') {
            setShowUsernamePicker(true);
          }
        } catch (e) {
          console.error('deferred profile fetch failed:', e);
        }
      }, 0);
    } catch (err) {
      console.error('Auth state change error:', err);
    }
  });

  // On tab-return, supabase-js can wedge after even a brief background (the token
  // refresh / visibility handling stalls), leaving the app stale - leaderboard,
  // profile, and game starts fail to load until a manual refresh. A "probe then
  // maybe reload" approach proved unreliable: the probe query can pass while the
  // real data calls still stall, so the failure slips through. A manual refresh
  // ALWAYS fixes it, so on resume we just DO that automatically: reload the page.
  // Gated to RELOAD_SAFE_SCREENS (home / leaderboard / locked - never mid-play),
  // so it can never interrupt a game; on those screens a reload is free and lands
  // on the resilient cold-load path that works every time.
  const RESUME_RELOAD_AWAY_MS = 1000; // ignore sub-second flickers; reload after any real switch
  let hiddenAt = null;
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      hiddenAt = Date.now();
      return;
    }
    if (document.visibilityState !== 'visible') return;
    const awayMs = hiddenAt ? Date.now() - hiddenAt : 0;
    hiddenAt = null;
    if (awayMs < RESUME_RELOAD_AWAY_MS) return; // quick flicker, leave alone
    if (!RELOAD_SAFE_SCREENS.has(screenRef.current)) return; // never reload mid-play
    window.location.reload(); // returned after real time away -> clean reload (= manual refresh)
  };
  document.addEventListener('visibilitychange', handleVisibilityChange);

  return () => {
    subscription.unsubscribe();
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
}, []);
  

  // ── Fetch games in progress (non-finished) ────────────────────────
  // Keyed on the id, NOT the currentUser object (see the cloud-streak effect).
  // This one matters most: an object dep tore down and resubscribed the
  // realtime channel on every auth event, and a mid-game tab-return could land
  // an H2H update inside the resubscribe gap where nobody was listening.
  useEffect(() => {
    const uid = currentUser?.id;
    if (!uid) { setGamesInProgress([]); return; }

    const fetchGames = async () => {
  try {
    const { data } = await supabase
      .from('matches')
      .select('id, mode, sport, status, player1_id, player2_id, player1_score, player2_score, created_at, sd_round, sd_scores')
      .or(`player1_id.eq.${uid},player2_id.eq.${uid}`)
      .not('status', 'eq', 'finished')
      .not('status', 'eq', 'expired')
      .order('created_at', { ascending: false });
    if (data) setGamesInProgress(prev => {
      const expiredIds = new Set(prev.filter(g => g.status === 'expired').map(g => g.id));
      return data.filter(g => !expiredIds.has(g.id));
    });
  } catch (err) {
    console.error('fetchGames error:', err);
  }
};

    fetchGames();
    const sub = supabase
      .channel('games-in-progress')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'matches' }, fetchGames)
      .subscribe();
    return () => supabase.removeChannel(sub);
  }, [currentUser?.id]);

  // ── Fetch recent finished games (for bell) ────────────────────────
  // Keyed on the id, NOT the currentUser object (see the cloud-streak effect).
  useEffect(() => {
    const uid = currentUser?.id;
    if (!uid) { setRecentFinished([]); return; }

    const fetchFinished = async () => {
      try {
        const cutoff = new Date(Date.now() - 72 * 3600 * 1000).toISOString();
        const { data } = await supabase
          .from('matches')
          .select('id, mode, sport, status, player1_id, player2_id, player1_score, player2_score, winner_id, created_at, finished_at')
          .or(`player1_id.eq.${uid},player2_id.eq.${uid}`)
          .eq('status', 'finished')
          .eq('player2_id', uid) // only games where opponent played (I was challenged)
          .gte('finished_at', cutoff)
          .order('finished_at', { ascending: false });

        // Also get games where I'm player1 and opponent finished
        const { data: data2 } = await supabase
          .from('matches')
          .select('id, mode, sport, status, player1_id, player2_id, player1_score, player2_score, winner_id, created_at, finished_at')
          .eq('player1_id', uid)
          .eq('status', 'finished')
          .not('player2_id', 'is', null)
          .gte('finished_at', cutoff)
          .order('finished_at', { ascending: false });

        const combined = [...(data || []), ...(data2 || [])];
        const unique = combined.filter((g, i, arr) => arr.findIndex(x => x.id === g.id) === i);
        setRecentFinished(unique);
      } catch (err) {
        console.error('fetchFinished error:', err);
      }
    };

    fetchFinished();
    const sub = supabase
      .channel('finished-games')
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'matches' }, fetchFinished)
      .subscribe();
    return () => supabase.removeChannel(sub);
  }, [currentUser?.id]);

  // ── Fetch opponent usernames ──────────────────────────────────────
  // Keyed on the id, NOT the currentUser object (see the cloud-streak effect).
  useEffect(() => {
    const uid = currentUser?.id;
    if (!uid) return;
    const allGames = [...gamesInProgress, ...recentFinished];
    const opponentIds = allGames
      .map(g => g.player1_id === uid ? g.player2_id : g.player1_id)
      .filter(Boolean);
    const uniqueIds = [...new Set(opponentIds)];
    if (!uniqueIds.length) return;

    supabase.from('profiles').select('id, username')
      .in('id', uniqueIds)
      .then(({ data }) => {
        if (!data) return;
        const map = {};
        data.forEach(p => { map[p.id] = p.username; });
        setGameOpponents(map);
      })
      .catch(err => console.error('fetchOpponents error:', err));
  }, [gamesInProgress, recentFinished, currentUser?.id]);

  // ── Bell count — finished games not yet seen ──────────────────────
  const activeWaitingOnMe = gamesInProgress.filter(g => {
    if (!g.player2_id) return false;
    
    // sd_pending — check if I've answered this SD round yet
    if (g.status === 'sd_pending' || g.status === 'sd_waiting') {
      const myPlayerNum = g.player1_id === currentUser?.id ? 1 : 2;
      const sdRound = g.sd_round ?? 0;
      const roundData = g.sd_scores?.find(r => r.round === sdRound);
      const iAnswered = myPlayerNum === 1 ? roundData?.p1 !== undefined : roundData?.p2 !== undefined;
      return !iAnswered;
    }

    const iPlayed = g.player1_id === currentUser?.id
      ? g.player1_score !== null
      : g.player2_score !== null;
    return !iPlayed;
  });
const cutoff48 = new Date(Date.now() - 48 * 3600 * 1000);
const activeWaitingOnThem = gamesInProgress.filter(g => {
  if (!g.player2_id) return false;
  if (new Date(g.created_at) < cutoff48) return false;
  if (g.status === 'sd_pending' || g.status === 'sd_waiting') {
    const myPlayerNum = g.player1_id === currentUser?.id ? 1 : 2;
    const sdRound = g.sd_round ?? 0;
    const roundData = g.sd_scores?.find(r => r.round === sdRound);
    const iAnswered = myPlayerNum === 1 ? roundData?.p1 !== undefined : roundData?.p2 !== undefined;
    return iAnswered;
  }
  const iPlayed = g.player1_id === currentUser?.id
    ? g.player1_score !== null
    : g.player2_score !== null;
  return iPlayed;
});
  const bellCount = recentFinished.filter(g => !lastBellCheck || new Date(g.finished_at) > lastBellCheck).length
  + activeWaitingOnMe.filter(g => {
      if (g.status === 'sd_pending' || g.status === 'sd_waiting') {
        return seenSdRounds[g.id] !== (g.sd_round ?? 0);
      }
      return !lastBellCheck || new Date(g.created_at) > lastBellCheck;
    }).length;

  const markAllSeen = async () => {
  const now = new Date();
  setLastBellCheck(now);
  setShowBell(false);
  // record current sd_round as seen for any active SD games
  const newSeen = {};
  activeWaitingOnMe
    .filter(g => g.status === 'sd_pending' || g.status === 'sd_waiting')
    .forEach(g => { newSeen[g.id] = g.sd_round ?? 0; });
  setSeenSdRounds(prev => ({ ...prev, ...newSeen }));
  if (currentUser) {
    try {
      await supabase.from('profiles')
        .update({ last_bell_check: now.toISOString() })
        .eq('id', currentUser.id);
    } catch (err) {
      console.error('markAllSeen error:', err);
    }
  }
};
  const getPlayersBySport = useCallback((sport) => {
    if (sport === 'nba') return { p: nbaPlayers, c: nbaColleges };
    if (sport === 'mosh') return { p: moshPlayers, c: moshColleges };
    return { p: players, c: colleges };
  }, [players, colleges, nbaPlayers, nbaColleges, moshPlayers, moshColleges]);
  // ── Join match ────────────────────────────────────────────────────
  useEffect(() => {
    async function joinMatch() {
      if (joined || !joinMode || !matchId) return;
      if (!currentUser || players.length === 0 || colleges.length === 0) return;

      try {
      const { data: match, error } = await supabase.from('matches').select('*').eq('id', matchId).single();
      if (error || !match) { setScreen(SCREENS.HOME); return; }
      const { p: matchPlayers, c: matchColleges } = getPlayersBySport(match.sport || 'nfl');
if (match.status === 'expired') { setScreen(SCREENS.EXPIRED); return; }
      if (match.status === 'finished') {
  const state = joinGame(match.seed, match.mode, matchPlayers, matchColleges, match.sport || 'nfl');
  if (!state) { setScreen(SCREENS.HOME); return; }
  state.matchId = match.id;
  state.sport = match.sport || 'nfl';
  state.playerNumber = match.player1_id === currentUser.id ? 1 : 2;
  state.score = match.player1_id === currentUser.id ? match.player1_score : match.player2_score;
  state.answers = (match.player1_id === currentUser.id ? match.player1_answers : match.player2_answers) || [];
  state.currentQuestion = TOTAL_QUESTIONS;
  state.phase = 'finished';
  state.match = match;
  state.startInSdPending = true; // triggers gameComplete=true in GameBoard
  setFreshGame(state);
  setScreen(SCREENS.PLAYING);
  setJoined(true);
  return;
}
      // Allow re-entry for sd_pending or sd_waiting matches
if (match.status === 'sd_pending' || match.status === 'sd_waiting') {
  const myPlayerNum = match.player1_id === currentUser.id ? 1 : 2;
  const mySdScore = match.sd_scores?.find(r => r.round === (match.sd_round ?? 0));
  const iAlreadyAnswered = myPlayerNum === 1 
    ? mySdScore?.p1 !== undefined 
    : mySdScore?.p2 !== undefined;
  if (iAlreadyAnswered) {
    // Already submitted this SD round — show waiting screen, not HOME
    const submittedSdScore = myPlayerNum === 1 ? mySdScore?.p1 : mySdScore?.p2;
    const state = joinGame(match.seed, match.mode, matchPlayers, matchColleges, match.sport || 'nfl');
    if (!state) { setScreen(SCREENS.HOME); return; }
    state.matchId = match.id;
    state.sport = match.sport || 'nfl';
    state.playerNumber = myPlayerNum;
    state.currentQuestion = TOTAL_QUESTIONS;
    state.phase = 'finished';
    state.match = match;
    state.score = (myPlayerNum === 1 ? match.player1_score : match.player2_score) ?? 0;
    state.answers = ((myPlayerNum === 1 ? match.player1_answers : match.player2_answers) || []);
    state.startInSdPending = true;
    state.alreadyAnsweredSd = true;
    state.mySubmittedSdScore = submittedSdScore ?? 0;
    setFreshGame(state);
    setScreen(SCREENS.PLAYING);
    setJoined(true);
    return;
  }
  // Fall through — let them play next SD round
}
if (match.status !== 'sd_pending' && match.status !== 'sd_waiting') {
  if (
    (match.player1_id === currentUser.id && match.player1_score !== null) ||
    (match.player2_id === currentUser.id && match.player2_score !== null)
  ) {
    // Clicking your own share link after playing used to dump you on HOME with
    // no explanation. Land on the Guy Off hub instead: the games dock shows
    // this match as WAITING with a POKE button, which answers the question the
    // click was asking. Mark the join handled, or the tab-return SIGNED_IN
    // re-fire (which mints a fresh currentUser object, a dep of this effect)
    // would re-run this and yank the user back here mid-browse.
    setJoined(true);
    setScreen(SCREENS.CREATE_MODE);
    return;
  }
}

      let matchRow = match;
      if (!match.player2_id && match.player1_id !== currentUser.id) {
        const { data: updatedMatch, error: updateError } = await supabase
          .from('matches').update({ player2_id: currentUser.id }).eq('id', matchId).select().single();
        if (updateError) { setScreen(SCREENS.HOME); return; }
        if (updatedMatch) matchRow = updatedMatch;
      }

      const state = joinGame(match.seed, match.mode, matchPlayers, matchColleges, match.sport || 'nfl');
  if (!state) { setScreen(SCREENS.HOME); return; }
  state.matchId = match.id;
  state.sport = match.sport || 'nfl';
  state.playerNumber = match.player1_id === currentUser.id ? 1 : 2;

      const myScore = matchRow.player1_id === currentUser.id
  ? matchRow.player1_score
  : matchRow.player2_score;
const myAnswers = (matchRow.player1_id === currentUser.id
  ? matchRow.player1_answers
  : matchRow.player2_answers) || [];

// Mid-game resume — has answers but regulation not yet complete
if (myAnswers.length > 0 && myAnswers.length < TOTAL_QUESTIONS && myScore === null &&
    matchRow.status !== 'sd_pending' && matchRow.status !== 'sd_waiting') {
  state.answers = myAnswers;
  state.currentQuestion = myAnswers.length;
  state.score = myAnswers.filter(a => a.isCorrect).length;
  // phase stays 'playing' — they resume mid-game, can't replay earlier questions
}

// Regulation complete — restore score and show results/SD screen
if (matchRow.status === 'sd_pending' || matchRow.status === 'sd_waiting' ||
    myScore !== null) {
  state.startInSdPending = true;
  state.sdRoundOnLoad = matchRow.sd_round ?? 0;
  state.score = myScore ?? state.score;
  state.answers = myAnswers.length > 0 ? myAnswers : state.answers;
  state.currentQuestion = TOTAL_QUESTIONS;
  state.phase = 'finished';
  state.match = matchRow;
}

      setFreshGame(state);
      setScreen(SCREENS.PLAYING);
      setJoined(true);
      } catch (err) {
        console.error('joinMatch error:', err);
        setScreen(SCREENS.HOME);
      }
    }
    joinMatch();
  }, [joinMode, matchId, currentUser, players, colleges, joined, getPlayersBySport]);

  // ── Handlers ──────────────────────────────────────────────────────
  const handleAuth = useCallback(async (user) => {
    // Close modal and set user immediately — don't wait on profile fetch
    setCurrentUser({ ...user, username: user.email?.split('@')[0] });
    setShowAuth(false);
    if (!joinMode) setShowLeaderboard(true);
    // Fetch profile in background to get real username + bell check
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('username, last_bell_check')
        .eq('id', user.id)
        .single();
      if (profile?.username) setCurrentUser(prev => ({ ...prev, username: profile.username }));
      if (profile?.last_bell_check) setLastBellCheck(new Date(profile.last_bell_check));
    } catch (err) {
      console.error('handleAuth profile fetch error:', err);
    }
  }, [joinMode]);

  const handleSignOut = useCallback(() => {
    signOut();
    setCurrentUser(null);
    setShowLeaderboard(false);
    setShowProfile(false);
  }, []);



  const handleCreateGame = useCallback(async (mode) => {
  if (isCreating) return;
  document.activeElement?.blur();
  if (!currentUser) { setShowAuth(true); return; }
  setCreateError(null);
  setIsCreating(true);
  try {
    const { p, c } = getPlayersBySport(h2hSport);
    if (!p || p.length === 0) {
      setCreateError('Still loading players - give it a second and try again.');
      return;
    }
    const state = createGame(mode, p, c, h2hSport);
    state.sport = h2hSport;
    console.log('[GuyOff] insert START', new Date().toISOString());
    const { data, error } = await fetchWithAuthRetry(
      () => supabase.from('matches')
        .insert({ seed: state.seed, mode, sport: h2hSport, player1_id: currentUser.id })
        .select().single(),
      { ms: 12000 }
    );
    if (error) { console.error('Failed to create match', error); setCreateError('Could not create the game. Try again.'); return; }
    console.log('[GuyOff] insert DONE', new Date().toISOString());
    state.matchId = data.id;
    state.playerNumber = 1;
    setFreshGame(state);
    setScreen(SCREENS.PLAYING);
  } catch (err) {
    console.error('handleCreateGame error:', err);
    setCreateError(err && err.message === 'timeout'
      ? "Connection's slow - try again"
      : 'Something went wrong creating the game. Try again.');
  } finally {
    console.log('[GuyOff] finally -> isCreating false');
    setIsCreating(false);
  }
  }, [h2hSport, getPlayersBySport, currentUser, isCreating]);

  const gameStateRef = useRef(gameState);
  useEffect(() => { gameStateRef.current = gameState; }, [gameState]);
  useEffect(() => { screenRef.current = screen; }, [screen]);

  const handleRematch = useCallback(async (passedMatchId) => {
  const targetMatchId = passedMatchId || gameStateRef.current?.matchId;
  if (!targetMatchId || !currentUser) return;

  try {
    const { data: oldMatch } = await supabase
      .from('matches').select('*').eq('id', targetMatchId).single();
    if (!oldMatch) return;

    // Check if a rematch already exists and hasn't been played yet
    const { data: existing } = await supabase.from('matches').select('*')
      .eq('player1_id', oldMatch.player1_id)
      .eq('player2_id', oldMatch.player2_id)
      .neq('id', oldMatch.id)
      .neq('status', 'finished')
      .order('created_at', { ascending: false })
      .limit(1).maybeSingle();

    let matchToJoin = existing;

    if (!matchToJoin) {
      const newSeed = Math.floor(Math.random() * 1000000);
      const { data: newMatch, error } = await supabase.from('matches')
        .insert({
          seed: newSeed,
          mode: oldMatch.mode,
          sport: oldMatch.sport || 'nfl',
          player1_id: oldMatch.player1_id,
          player2_id: oldMatch.player2_id,
        })
        .select().single();
      if (error) { console.error('Rematch failed:', error); return; }
      matchToJoin = newMatch;
    }

    // Use joinGame not createGame — same deterministic output for both players
    const { p, c } = getPlayersBySport(matchToJoin.sport || 'nfl');
    const state = joinGame(matchToJoin.seed, matchToJoin.mode, p, c, matchToJoin.sport || 'nfl');
    state.sport = matchToJoin.sport || 'nfl';
    if (!state) return;
    state.matchId = matchToJoin.id;
    state.playerNumber = oldMatch.player1_id === currentUser.id ? 1 : 2;

    setGameState(null);
    setScreen(SCREENS.HOME);
    setTimeout(() => {
      setJoined(true);
      setFreshGame(state);
      setScreen(SCREENS.PLAYING);
    }, 50);
  } catch (err) {
    console.error('handleRematch error:', err);
  }
}, [currentUser, getPlayersBySport]);

const handleCreateDailyChallenge = useCallback(async (e) => {
  if (isCreating) return;
  e?.currentTarget?.blur();
  if (hasPlayedToday()) { setLockedSport('nfl'); setScreen(SCREENS.DAILY_LOCKED); return; }
  setIsCreating(true);
  try {
    const state = await createDailyChallenge(players, colleges);
    const progress = getDailyProgress();
    if (progress && progress.currentQuestion > 0) {
      state.answers = progress.answers;
      state.currentQuestion = progress.currentQuestion;
      state.score = progress.score;
    }
    if (state.currentQuestion > 0) {
      setFreshGame(state);                                // resuming a partial daily: straight to the game (unchanged behavior)
      setScreen(SCREENS.PLAYING);
    } else {
      setFreshGame({ ...state, skipOpeningBreak: true }); // fresh daily: App-level splash replaces GameBoard's opening break
      setScreen(SCREENS.NFL_BEGIN);
    }
  } catch (err) {
    console.error('NFL daily challenge error:', err);
    setScreen(SCREENS.HOME); // deep-link LOADING-mask fallback; no-op for the HOME button
  } finally {
    setIsCreating(false);
  }
}, [players, colleges, isCreating]);

const handleGoHome = useCallback(() => {
  setGameState(null);
  setScreen(SCREENS.HOME);
  setJoinError('');
  setDailyData(getDailyData());
  if (window.location.pathname !== '/') {
    window.history.pushState({}, '', '/');
  }
}, []);

const handleCreateNBADailyChallenge = useCallback(async (e) => {
  if (isCreating) return;
  e?.currentTarget?.blur();
  if (!currentUser) { setShowAuth(true); return; }
  if (hasPlayedNBAToday()) { setLockedSport('nba'); setScreen(SCREENS.DAILY_LOCKED); return; }
  setIsCreating(true);
  try {
    const state = await createNBADailyChallenge(nbaPlayers, nbaColleges);
    const progress = getNBADailyProgress();
    if (progress && progress.currentQuestion > 0) {
      state.answers = progress.answers;
      state.currentQuestion = progress.currentQuestion;
      state.score = progress.score;
    }
    setFreshGame(state);
    setScreen(SCREENS.PLAYING);
  } catch (err) {
    console.error('NBA daily challenge error:', err);
  } finally {
    setIsCreating(false);
  }
}, [nbaPlayers, nbaColleges, currentUser, isCreating]);

const handleCreateMoshDailyChallenge = useCallback(async (e) => {
  if (isCreating) return;
  e?.currentTarget?.blur();
  if (!currentUser) { setShowAuth(true); return; }
  if (hasPlayedMoshToday()) { setLockedSport('mosh'); setScreen(SCREENS.DAILY_LOCKED); return; }
  setIsCreating(true);
  try {
    const state = await createMoshDailyChallenge(moshPlayers, moshColleges);
    const progress = getMoshDailyProgress();
    if (progress && progress.currentQuestion > 0) {
      state.answers = progress.answers;
      state.currentQuestion = progress.currentQuestion;
      state.score = progress.score;
    }
    setFreshGame(state);
    setScreen(SCREENS.PLAYING);
  } catch (err) {
    console.error('Mosh daily challenge error:', err);
  } finally {
    setIsCreating(false);
  }
}, [moshPlayers, moshColleges, currentUser, isCreating]);

const handleCreateNTGDaily = useCallback(async (e) => {
  if (isCreating) return;
  e?.currentTarget?.blur();
  if (hasPlayedNTGToday()) { setLockedSport('ntg'); setScreen(SCREENS.DAILY_LOCKED); return; }
  setDailyError(null);
  setIsCreating(true);
  try {
    const q = await fetchWithAuthRetry(() => fetchTodaysNTGQuestion(), { ms: 4000 });
    if (!q) { setScreen(SCREENS.HOME); setDailyError("Couldn't load today's question - try again"); return; }
    setNtgQuestion(q);
    setScreen(SCREENS.NTG_BEGIN);
  } catch (err) {
    console.error('Name That Guy error:', err);
    setScreen(SCREENS.HOME); // deep-link LOADING-mask fallback; no-op for the HOME button
    setDailyError(err && err.message === 'timeout'
      ? "Connection's slow - try again"
      : "Couldn't start Name That Guy - try again");
  } finally {
    setIsCreating(false);
  }
}, [isCreating]);

const handleCreateNTGSrDaily = useCallback(async (e) => {
  if (isCreating) return;
  e?.currentTarget?.blur();
  if (hasPlayedNTGSrToday()) { setLockedSport('ntg_sr'); setScreen(SCREENS.DAILY_LOCKED); return; }
  setDailyError(null);
  setIsCreating(true);
  try {
    const p = await fetchWithAuthRetry(() => fetchTodaysNTGSrPuzzle(), { ms: 4000 });
    if (!p) { setScreen(SCREENS.HOME); setDailyError("Couldn't load today's puzzle - try again"); return; }
    setNtgSrPuzzle(p);
    setScreen(SCREENS.NTG_SR_BEGIN);
  } catch (err) {
    console.error('Name That Guy Sr. error:', err);
    setScreen(SCREENS.HOME);
    setDailyError(err && err.message === 'timeout'
      ? "Connection's slow - try again"
      : "Couldn't start Name That Guy Sr. - try again");
  } finally {
    setIsCreating(false);
  }
}, [isCreating]);


const handleCreateDigitsDaily = useCallback(async (e) => {
  if (isCreating) return;
  e?.currentTarget?.blur();
  if (!currentUser) { setShowAuth(true); return; }
  if (hasPlayedDigitsToday()) { setLockedSport('digits'); setScreen(SCREENS.DAILY_LOCKED); return; }
  setDailyError(null);
  setIsCreating(true);
  try {
    const q = await fetchWithAuthRetry(() => fetchTodaysDigitsQuestion(), { ms: 4000 });
    if (!q) { setScreen(SCREENS.HOME); setDailyError("Couldn't load today's number - try again"); return; }
    setDigitsQuestion(q);
    setScreen(SCREENS.DIGITS_BEGIN);
  } catch (err) {
    console.error('Digits error:', err);
    setScreen(SCREENS.HOME);
    setDailyError(err && err.message === 'timeout'
      ? "Connection's slow - try again"
      : "Couldn't start Digits - try again");
  } finally {
    setIsCreating(false);
  }
}, [isCreating, currentUser]);

// Results screens offer the other dailies ("more free dailies"); route by key.
const handlePlayDaily = useCallback((key) => {
  const go = { nfl: handleCreateDailyChallenge, nba: handleCreateNBADailyChallenge,
               mosh: handleCreateMoshDailyChallenge, jr: handleCreateNTGDaily, sr: handleCreateNTGSrDaily,
               digits: handleCreateDigitsDaily }[key];
  if (go) go();
}, [handleCreateDailyChallenge, handleCreateNBADailyChallenge, handleCreateMoshDailyChallenge, handleCreateNTGDaily, handleCreateNTGSrDaily, handleCreateDigitsDaily]);

  // Deep-link entry: a cold /ntg, /nfl or /sr load lands on that mode's BEGIN splash.
  // Runs ALONGSIDE (never inside) onAuthStateChange and the reload watchdogs -- it only
  // reads authReady + pathname and calls the same handlers the buttons use, so the
  // played-today -> DAILY_LOCKED guard is inherited. Mask the fetch with LOADING (not the
  // HOME menu) so cold ad traffic never sees a menu flash. One-shot per load.
  const deepLinkDoneRef = useRef(false);
  useEffect(() => {
    if (deepLinkDoneRef.current || joinMode) return;
    if (!authReady) return;                 // after auth settles (same gate as the cloud-streak boot fetch)
    if (screen !== SCREENS.HOME) return;    // only from the settled boot HOME
    const path = window.location.pathname;
    if (path === '/ntg') {
      deepLinkDoneRef.current = true;
      setScreen(SCREENS.LOADING);
      handleCreateNTGDaily();
    } else if (path === '/nfl') {
      deepLinkDoneRef.current = true;
      setScreen(SCREENS.LOADING);
      handleCreateDailyChallenge();
    } else if (path === '/sr') {
      deepLinkDoneRef.current = true;
      setScreen(SCREENS.LOADING);
      handleCreateNTGSrDaily();
    } else if (path === '/profile') {
      // Where the StreakWard claim emails point. Unlike the mode deep links this
      // sets NO screen: the profile is a panel over HOME. It relies on this
      // effect's `screen === HOME` gate, which means it runs AFTER the [screen]
      // effect that blanket-resets setShowProfile(false) -- open it any earlier
      // and that reset closes it again on the way through boot.
      deepLinkDoneRef.current = true;
      setShowProfile(true);
      // The panel only renders when signed in, so a reader opening the email on
      // a device with no session gets auth first; showProfile stays queued and
      // the panel appears once they're through.
      if (!currentUser) setShowAuth(true);
    }
  }, [authReady, screen, joinMode, currentUser, handleCreateNTGDaily, handleCreateDailyChallenge, handleCreateNTGSrDaily]);

  // ── Signup attribution ────────────────────────────────────────────
  // Stamps the first-touch UTM/referrer (captured at page entry in index.js)
  // onto the profile row. Runs ALONGSIDE (never inside) the auth handler and
  // the reload watchdogs. Every signup path - email, Google SSO, guest claim -
  // funnels through setCurrentUser, so keying on the id covers all three with
  // no hook in the signed-in branch.
  //
  // This fires on EVERY session start, not just signup (cold loads and the
  // resume reload both re-run it), so the write is guarded three ways:
  //   - a per-id ref, so a session fires at most one request
  //   - .is('signup_source', null), so the DB only accepts the first touch
  //   - .gt('created_at', cutoff), so only a genuinely NEW account attributes.
  // That last guard is the important one: every pre-existing user also has a
  // null signup_source, so without it an account from months ago would get
  // credited to whatever tagged link they happened to click, inflating
  // campaign counts with people who joined long before the campaign ran.
  // profiles.created_at is stamped by the signup trigger (verified within 5s
  // of auth signup for all 1812 recent accounts), and every signup path -
  // email, Google SSO, guest claim - writes within seconds of it, so the
  // hour-wide window is generous even for a slow OAuth round trip.
  const attributionDoneRef = useRef(new Set());
  useEffect(() => {
    const uid = currentUser?.id;
    if (!authReady || !uid) return;
    if (attributionDoneRef.current.has(uid)) return;
    attributionDoneRef.current.add(uid);

    try {
      const attr = readAttribution();
      if (!attr) return;
      if (isAttributionSynced(uid, attr)) return;
      supabase
        .from('profiles')
        .update({
          signup_source: attr.source,
          signup_medium: attr.medium,
          signup_campaign: attr.campaign,
          signup_content: attr.content,
          signup_term: attr.term,
          signup_landing_page: attr.landing_page,
          signup_referrer: attr.referrer,
          signup_attributed_at: new Date().toISOString(),
        })
        .eq('id', uid)
        .is('signup_source', null)
        .gt('created_at', new Date(Date.now() - NEW_ACCOUNT_WINDOW_MS).toISOString())
        .then(
          ({ error }) => {
            if (!error) markAttributionSynced(uid, attr);
          },
          () => {}
        );
    } catch (e) {
      // attribution must never block or delay a signup
    }
  }, [authReady, currentUser?.id]);

  // ── Top Nav ───────────────────────────────────────────────────────
  const renderTopNav = () => (
    <div className="top-nav">
      <span className="top-nav-logo" onClick={handleGoHome} style={{ cursor: 'pointer' }}>KYG<sup style={{ fontSize: '7px', verticalAlign: 'super', marginLeft: '1px', opacity: 0.6 }}>™</sup></span>
      <div className="top-nav-auth">
        {currentUser ? (
          <>
            <span className="top-nav-username">{currentUser.username || currentUser.email?.split('@')[0]}</span>
            <div className="bell-wrapper" ref={bellRef}>
              <button className="top-nav-btn bell-btn" onClick={() => {
                setShowBell(v => !v);
              }}>
                🔔
                {bellCount > 0 && <span className="bell-badge">{bellCount}</span>}
              </button>
              {showBell && (
                <div className="bell-dropdown" style={{ right: 0, left: 'auto' }}>
                  <div className="bell-dropdown-scroll">
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 12px', borderBottom: '1px solid var(--border-dark)', position: 'sticky', top: 0, background: 'var(--bg-card)', zIndex: 1 }}>
    <span style={{ fontFamily: 'var(--font-heading)', fontSize: '10px', color: 'var(--text-dim)', letterSpacing: '1px', minWidth: '50px' }}>STATUS</span>
<span style={{ fontFamily: 'var(--font-heading)', fontSize: '10px', color: 'var(--text-dim)', letterSpacing: '1px', flex: 1, textAlign: 'center' }}>OPP</span>
    {bellCount > 0
      ? <button className="bell-mark-read" onClick={markAllSeen} style={{ margin: 0 }}>MARK ALL READ</button>
      : <span style={{ fontFamily: 'var(--font-heading)', fontSize: '10px', color: 'var(--text-dim)', letterSpacing: '2px', width: '60px', textAlign: 'right' }}>RESULTS</span>
    }
  </div>
                    {[...activeWaitingOnMe].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map(game => {
  const opponentId = game.player1_id === currentUser.id ? game.player2_id : game.player1_id;
  const opponentName = gameOpponents[opponentId] || '...';
  return (
    <button
  key={game.id}
  className="bell-game-row bell-game-unseen"
  onClick={async () => {
  const now = new Date();
  setLastBellCheck(now);
  try {
    if (currentUser) {
      await supabase.from('profiles').update({ last_bell_check: now.toISOString() }).eq('id', currentUser.id);
    }
  } catch (err) {
    console.error('Bell nav update error:', err);
  }
  setJoined(false);
  window.location.href = `/join/${game.id}`;
}}
  style={{ display: 'flex', alignItems: 'center', width: '100%' }}
>
      <span style={{ minWidth: '36px', fontSize: '14px' }}>
  {(game.status === 'sd_pending' || game.status === 'sd_waiting')
    ? (seenSdRounds[game.id] !== (game.sd_round ?? 0) ? '📩' : '📭')
    : (lastBellCheck && new Date(game.created_at) <= lastBellCheck ? '📭' : '📩')}
</span>
<div className="bell-game-opponent" style={{ flex: 1, textAlign: 'center' }}>{opponentName}</div>
<div className="bell-game-meta" style={{ minWidth: '60px', textAlign: 'right' }}>
  <span className="bell-game-status bell-status-win">YOUR TURN</span>
</div>
    </button>
  );
})}
                   {activeWaitingOnThem.map(game => {
  const opponentId = game.player1_id === currentUser.id ? game.player2_id : game.player1_id;
  const opponentName = gameOpponents[opponentId] || '...';
  return (
    <button
      key={game.id}
      className="bell-game-row"
      style={{ display: 'flex', alignItems: 'center', width: '100%' }}
    >
      <span style={{ minWidth: '36px', fontSize: '14px' }}>⏳</span>
      <div className="bell-game-opponent" style={{ flex: 1, textAlign: 'center' }}>{opponentName}</div>
      <div className="bell-game-meta" style={{ minWidth: '60px', textAlign: 'right' }}>
        <button
          className="gip-copy-btn"
          style={{ borderColor: 'var(--gold)', color: 'var(--gold)', fontSize: '10px', padding: '3px 7px' }}
          onClick={(e) => {
            e.stopPropagation();
            const text = `Yo, I already played — waiting on you. Think you can keep up? knowyourguy.com/join/${game.id}`;
            if (navigator.share) navigator.share({ title: 'Know Your Guy', text });
            else navigator.clipboard.writeText(text).then(() => alert('Copied!'));
          }}
        >POKE</button>
      </div>
    </button>
  );
})}
{recentFinished.length === 0 && activeWaitingOnMe.length === 0 ? (
  <div className="bell-empty">No recent results</div>
) : [...recentFinished].sort((a, b) => new Date(b.finished_at) - new Date(a.finished_at)).map(game => {
                      const opponentId = game.player1_id === currentUser.id ? game.player2_id : game.player1_id;
                      const opponentName = gameOpponents[opponentId] || '...';
                      const iWon = game.winner_id === currentUser.id;
                      const isTie = !game.winner_id;
                      const unseen = !lastBellCheck || new Date(game.finished_at) > lastBellCheck;
                      const timeAgo = (() => {
                        const mins = Math.floor((Date.now() - new Date(game.finished_at)) / 60000);
                        if (mins < 60) return `${mins}m ago`;
                        const hrs = Math.floor(mins / 60);
                        if (hrs < 24) return `${hrs}h ago`;
                        return `${Math.floor(hrs / 24)}d ago`;
                      })();
                      return (
  <button
  key={game.id}
  className={`bell-game-row ${unseen ? 'bell-game-unseen' : ''}`}
  onClick={async () => {
  const now = new Date();
  setLastBellCheck(now);
  try {
    if (currentUser) {
      await supabase.from('profiles').update({ last_bell_check: now.toISOString() }).eq('id', currentUser.id);
    }
  } catch (err) {
    console.error('Bell results nav error:', err);
  }
  setShowBell(false);
  setJoined(false);
  window.location.href = `/join/${game.id}`;
}}
  style={{ display: 'flex', alignItems: 'center', width: '100%', cursor: 'pointer', textAlign: 'left', background: 'none', border: 'none' }}
>
    <span style={{ minWidth: '36px', fontSize: '14px' }}>{unseen ? '✉️' : '📭'}</span>
<div className="bell-game-opponent" style={{ flex: 1, textAlign: 'center' }}>{opponentName}</div>
<div className="bell-game-meta" style={{ minWidth: '60px', textAlign: 'right' }}>
  <span className={`bell-game-status ${iWon ? 'bell-status-win' : isTie ? '' : 'bell-status-loss'}`}>
    {iWon ? 'WIN' : isTie ? 'TIE' : 'LOSS'}
  </span>
  <span className="bell-game-time">{timeAgo}</span>
</div>
  </button>
);
                    })}
                  </div>
                </div>
              )}
            </div>
            <button className="top-nav-btn" onClick={() => { setShowLeaderboard(false); setShowProfile(v => !v); }}>PROFILE</button>
            <button className="top-nav-btn" onClick={() => { setShowProfile(false); setShowLeaderboard(v => !v); }}>LEADERBOARD</button>
            <button className="top-nav-btn top-nav-signout" onClick={handleSignOut}>SIGN OUT</button>
          </>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontFamily: 'var(--font-heading)', fontSize: '11px', color: 'var(--gold)', letterSpacing: '1px' }}>Top 25 Material?</span>
            <button className="top-nav-btn top-nav-signin" onClick={() => setShowAuth(true)}>JOIN THE LEADERBOARD</button>
          </div>
        )}
      </div>
    </div>
  );

  // ── GIP Panel (used in CREATE_MODE screen) ────────────────────────
  const renderGIP = () => {
    const cutoff = Date.now() - 72 * 3600 * 1000;

    // Pending = I created, no opponent yet
    const pending = gamesInProgress.filter(g =>
      g.player1_id === currentUser?.id && !g.player2_id &&
      new Date(g.created_at) > new Date(cutoff)
    );

    // Active = both players engaged, not finished
    const active = gamesInProgress.filter(g =>
      g.player2_id &&
      new Date(g.created_at) > new Date(cutoff)
    );

    // Recent results
    const results = recentFinished.slice(0, 10);

    const timeAgo = (ts) => {
      const mins = Math.floor((Date.now() - new Date(ts)) / 60000);
      if (mins < 60) return `${mins}m ago`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return `${hrs}h ago`;
      return `${Math.floor(hrs / 24)}d ago`;
    };

    const copyLink = (id) => {
      const url = `${window.location.origin}/join/${id}`;
      navigator.clipboard.writeText(url).then(() => alert('Link copied!'));
    };

    return (
      <div className="gip-panel">

        {/* Pending Invites */}
        {pending.length > 0 && (
          <div className="gip-section">
            <div className="gip-section-title">WAITING FOR A FRIEND <span style={{ fontWeight: 400, opacity: 0.5 }}>(invites expire after 48hrs)</span></div>
            {pending.map(g => (
  <div key={g.id} className="gip-row">
    <div className="gip-row-info">
      <span className="gip-row-label">{g.sport === 'nba' ? '🏀' : g.sport === 'mosh' ? '🍲' : '🏈'} MISSING A FRIEND? FIND ONE AND SHARE...</span>
      <span className="gip-row-time">{timeAgo(g.created_at)}</span>
    </div>
    <div style={{ display: 'flex', gap: '6px' }}>
      <button className="gip-copy-btn" onClick={() => copyLink(g.id)}>
        COPY LINK
      </button>
      <button
        className="gip-copy-btn"
        style={{ borderColor: 'var(--red)', color: 'var(--red)' }}
        onClick={async () => {
  setGamesInProgress(prev => prev.filter(x => x.id !== g.id));
  try {
    const { error } = await supabase.from('matches').update({ status: 'expired' }).eq('id', g.id);
    if (error) console.error('Failed to expire game:', error);
  } catch (err) {
    console.error('Expire game error:', err);
  }
}}
      >
        ✕
      </button>
    </div>
  </div>

            ))}
          </div>
        )}

        {/* Active Games */}
        {active.length > 0 && (
          <div className="gip-section">
            <div className="gip-section-title">ACTIVE</div>
            {active.map(g => {
              const opponentId = g.player1_id === currentUser?.id ? g.player2_id : g.player1_id;
              const opponentName = gameOpponents[opponentId] || '...';
              let iPlayed;
if (g.status === 'sd_pending' || g.status === 'sd_waiting') {
  const myPlayerNum = g.player1_id === currentUser?.id ? 1 : 2;
  const sdRoundNum = g.sd_round ?? 0;
  const roundData = g.sd_scores?.find(r => r.round === sdRoundNum);
  iPlayed = myPlayerNum === 1 ? roundData?.p1 !== undefined : roundData?.p2 !== undefined;
} else {
  iPlayed = g.player1_id === currentUser?.id ? g.player1_score !== null : g.player2_score !== null;
}
              return (
                <div key={g.id} className="gip-row">
                  <div className="gip-row-info">
                    <span className="gip-row-opponent">{g.sport === 'nba' ? '🏀' : g.sport === 'mosh' ? '🍲' : '🏈'} {opponentName}</span>
                    <span className="gip-row-time">{timeAgo(g.created_at)}</span>
                  </div>
                  {iPlayed ? (
  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
    <button
      className="gip-copy-btn"
      style={{ borderColor: 'var(--gold)', color: 'var(--gold)' }}
      onClick={() => {
        const text = `Yo, yo... waiting on you. Keep up 👉 knowyourguy.com/join/${g.id}`;
        if (navigator.share) {
          navigator.share({ title: 'Know Your Guy Alert', text });
        } else {
          navigator.clipboard.writeText(text).then(() => alert('Copied!'));
        }
      }}
    >
      POKE
    </button>
    <span className="gip-row-status gip-status-waiting">WAITING</span>
  </div>
) : (
                    <button
                      className="gip-copy-btn"
                      style={{ borderColor: 'var(--green-correct)', color: 'var(--green-correct)' }}
                      onClick={() => { setJoined(false); window.location.href = `/join/${g.id}`; }}
                    >
                      PLAY
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Recent Results */}
        {results.length > 0 && (
          <div className="gip-section">
            <div className="gip-section-title">RECENT RESULTS</div>
            {results.map(g => {
              const opponentId = g.player1_id === currentUser?.id ? g.player2_id : g.player1_id;
              const opponentName = gameOpponents[opponentId] || '...';
              const iWon = g.winner_id === currentUser?.id;
              const isTie = !g.winner_id;
              return (
                <div key={g.id} className="gip-row">
                  <div className="gip-row-info">
                    <span className="gip-row-opponent">{g.sport === 'nba' ? '🏀' : g.sport === 'mosh' ? '🍲' : '🏈'} {opponentName}</span>
<span className="gip-row-time">{g.mode?.toUpperCase()} MODE • {timeAgo(g.created_at)}</span>
                    <span className={`gip-result-badge ${iWon ? 'gip-result-win' : isTie ? 'gip-result-tie' : 'gip-result-loss'}`}>
                      {iWon ? 'W' : isTie ? 'T' : 'L'}
                    </span>
                  </div>
                  <button className="gip-rematch-btn" onClick={() => handleRematch(g.id)}>
                    REMATCH
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {pending.length === 0 && active.length === 0 && results.length === 0 && (
          <div className="gip-empty">No games yet. Create one above!</div>
        )}
      </div>
    );
  };

  // ── Screens ───────────────────────────────────────────────────────
  if (screen === SCREENS.LOADING) {
    return (
      <div className="app" key={screen}>
        <div className="loading-screen">
          <div className="loading-spinner" />
          <h1 className="app-title">KNOW YOUR GUY</h1>
          <p className="loading-text">{joinMode ? 'JOINING MATCH...' : 'LOADING ROSTERS...'}</p>
          {joinMode && players.length > 0 && !currentUser && (
            <div style={{ marginTop: '24px' }}>
              <p style={{ color: '#aaa', marginBottom: '12px' }}>Sign in to join the match</p>
              <button className="btn btn-primary" onClick={() => setShowAuth(true)}>SIGN IN / SIGN UP</button>
            </div>
          )}
        </div>
        {/* This screen pointed people at a SIGN IN button but never rendered the
            modal, so every signed-out visitor on a /join/ link got a dead button
            and an endless spinner. The modal has to live on THIS screen. */}
        {showAuth && <AuthModal onAuth={handleAuth} onClose={() => setShowAuth(false)} />}
        {showUsernamePicker && currentUser && <UsernamePickerModal user={currentUser} onDone={(name) => { setCurrentUser(prev => ({ ...prev, username: name })); setShowUsernamePicker(false); }} />}
      </div>
    );
  }

  if (screen === SCREENS.LOAD_ERROR) {
    return (
      <div className="app" key={screen}>
        <div className="loading-screen">
          <h1 className="app-title">KNOW YOUR GUY</h1>
          <p className="loading-text">Couldn't load. Check your connection.</p>
          <button className="btn btn-primary" style={{ marginTop: '16px' }} onClick={loadRosters}>
            TAP TO RETRY
          </button>
        </div>
        {showAuth && <AuthModal onAuth={handleAuth} onClose={() => setShowAuth(false)} />}
        {showUsernamePicker && currentUser && <UsernamePickerModal user={currentUser} onDone={(name) => { setCurrentUser(prev => ({ ...prev, username: name })); setShowUsernamePicker(false); }} />}
      </div>
    );
  }

  if (screen === SCREENS.HOME) {
    return (
      <div className="app" key={screen}>
        {renderTopNav()}
        {currentUser && showProfile && (
  <div ref={profileRef}>
    <Profile
      user={currentUser}
      authReady={authReady}
      onUsernameChange={(name) => setCurrentUser(prev => ({ ...prev, username: name }))}
    />
  </div>
)}
{currentUser && showLeaderboard && (
  <div ref={leaderboardRef}>
    <Leaderboard currentUser={currentUser} authReady={authReady} />
  </div>
)}
        <div className="home-screen">
          {/* Announcement banner — currently promoting community Sr. submissions (2026-08-06).
              Clickable: same gate as every submit CTA (signed-out -> auth modal). */}
          {SHOW_UPDATE_BANNER && (
            <div
              onClick={() => (currentUser ? setShowSrSubmit(true) : setShowAuth(true))}
              style={{
                margin: '0 0 16px 0',
                padding: '8px 14px',
                background: 'var(--bg-card)',
                border: '1px solid var(--gold)',
                borderRadius: 'var(--radius)',
                textAlign: 'center',
                fontFamily: 'var(--font-body)',
                fontSize: '12px',
                lineHeight: 1.45,
                color: 'var(--text-dim)',
                cursor: 'pointer',
              }}>
              <span style={{ color: 'var(--gold)' }}>Want to write the next <span style={{ color: '#ff3b30', fontWeight: 900 }}>SR.</span>? Send it Matt's way <span style={{ opacity: 0.75 }}>(click)</span></span>
            </div>
          )}
          {/* RESERVED ZONE: the band between the nav rule and the hero is earmarked for a
              future icon/announcement banner (the dormant SHOW_UPDATE_BANNER block above
              renders here too). Keep this area clear of permanent content. */}
          <div className="home-header">
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '6px' }}>
              <div style={{ position: 'relative', display: 'inline-block' }} ref={infoRef}>
                <button
                  onClick={() => setShowInfo(v => !v)}
                  style={{ background: 'none', border: '1px solid #1a6bbf', borderRadius: '50%', color: '#4a9eff', fontSize: '9px', fontWeight: 700, width: '13px', height: '13px', cursor: 'pointer', lineHeight: '11px', padding: 0, opacity: 0.7 }}
                >i</button>
                {showInfo && (
                  <div style={{ position: 'absolute', top: '22px', left: '50%', transform: 'translateX(-50%)', background: 'var(--bg-card)', border: '1px solid var(--border-dark)', borderRadius: 'var(--radius)', padding: '12px 16px', zIndex: 100, minWidth: '220px', boxShadow: '0 4px 16px rgba(0,0,0,0.4)' }}>
                   <p style={{ fontFamily: 'var(--font-body)', fontSize: '13px', color: 'var(--text-white)', margin: '0 0 6px 0' }}>The painting was a gift, Todd. I'm taking it with me.</p>
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px' }}>
  <span style={{
    fontFamily: 'var(--font-heading)',
    fontSize: '11px',
    color: 'var(--gold)',
    letterSpacing: '2px',
  }}>
    Stay Streakin'
  </span>
</div>
            <h1 className="app-title">KNOW YOUR GUY</h1>
            <div className="title-stripe" />
          </div>
          <div className="coach-quote">
            <p className="coach-quote-text">&ldquo;{coachQuote.quote}&rdquo;</p>
            <p className="coach-quote-author">&mdash; {(() => {
              const m = coachQuote.coach.match(/^(.*?)\s*\((.*)\)$/);
              return m ? <>{m[1]} <span className="coach-quote-movie">({m[2]})</span></> : coachQuote.coach;
            })()}</p>
          </div>
  <div className="home-actions" style={{ gap: '12px', display: 'flex', flexDirection: 'column' }}>
  <p className="home-actions-label">Match guys to their college</p>
  <button className="btn btn-daily btn-large" onClick={handleCreateDailyChallenge} disabled={isCreating}>
    <span className="btn-main-text">🏈 NFL DAILY CHALLENGE</span>
    <span className="btn-hint">
      {!currentUser && hasPlayedToday() && getDailyData()?.lastScore >= 3
      ? 'SIGN IN / SIGN UP TO KEEP YOUR STREAK'
      : hasPlayedToday() && getDailyData()?.lastScore >= 3
      ? (cloudStreak > 0 ? `${cloudStreak}-DAY STREAK` : 'PLAYED TODAY')
      : hasPlayedToday()
      ? 'PLAYED TODAY. WARM UP AND TRY AGAIN TOMORROW'
      : cloudStreak > 0
      ? `${cloudStreak}-DAY STREAK`
      : '5 QUESTIONS · 7 SECONDS EACH · EVERY DAY'}
    </span>
  </button>
  <button className="btn btn-daily btn-large" onClick={handleCreateNBADailyChallenge} disabled={isCreating} style={{ opacity: currentUser ? 1 : 0.6 }}>
    <span className="btn-main-text">🏀 NBA DAILY CHALLENGE</span>
    <span className="btn-hint">
      {!currentUser
      ? 'SIGN IN TO UNLOCK'
      : hasPlayedNBAToday() && nbaCloudStreak > 0
      ? `${nbaCloudStreak}-DAY STREAK`
      : hasPlayedNBAToday()
      ? 'PLAYED TODAY. WARM UP AND TRY AGAIN TOMORROW'
      : nbaCloudStreak > 0
      ? `${nbaCloudStreak}-DAY STREAK`
      : '5 QUESTIONS · 7 SECONDS EACH · EVERY DAY'}
    </span>
  </button>
  <button className="btn btn-daily btn-large" onClick={handleCreateMoshDailyChallenge} disabled={isCreating} style={{ opacity: currentUser ? 1 : 0.6 }}>
    <span className="btn-main-text">🍲 MISH MOSH DAILY CHALLENGE</span>
    <span style={{ fontFamily: 'var(--font-body)', fontSize: '10px', color: 'rgba(201,168,76,0.6)', letterSpacing: '1px' }}>(MLB · NHL · Celebs · Politicians · more)</span>
    <span className="btn-hint">
      {!currentUser
      ? 'SIGN IN TO UNLOCK'
      : hasPlayedMoshToday() && moshCloudStreak > 0
      ? `${moshCloudStreak}-DAY STREAK`
      : hasPlayedMoshToday()
      ? 'PLAYED TODAY. WARM UP AND TRY AGAIN TOMORROW'
      : moshCloudStreak > 0
      ? `${moshCloudStreak}-DAY STREAK`
      : '5 QUESTIONS · 7 SECONDS EACH · EVERY DAY'}
    </span>
  </button>
  <p className="home-actions-label">Daily guy trivia</p>
  <button className="btn btn-daily btn-large" onClick={handleCreateNTGSrDaily} disabled={isCreating}>
    <span className="btn-main-text">👴 NAME THAT GUY <span style={{ color: '#ff3b30', fontWeight: 900 }}>SR.</span> DAILY CHALLENGE</span>
    <span className="btn-hint">
      {!currentUser && hasPlayedNTGSrToday() && getNTGSrDailyData()?.lastScore >= 1
      ? 'SIGN IN / SIGN UP TO KEEP YOUR STREAK'
      : hasPlayedNTGSrToday() && getNTGSrDailyData()?.lastScore >= 1
      ? (ntgSrCloudStreak > 0 ? `${ntgSrCloudStreak}-DAY STREAK` : 'PLAYED TODAY')
      : hasPlayedNTGSrToday()
      ? 'PLAYED TODAY. WARM UP AND TRY AGAIN TOMORROW'
      : ntgSrCloudStreak > 0
      ? `${ntgSrCloudStreak}-DAY STREAK`
      : '1 GUY · 3 GUESSES · EVERY DAY'}
    </span>
    {currentUser && (
      <span className="btn-hint" style={{ marginTop: '2px', textTransform: 'none' }}>
        stri<span style={{ color: '#ff3b30', fontWeight: 900, fontSize: '1.1em', verticalAlign: 'middle' }}>K</span>e: {ntgSrCloudStrikes >= 1 ? ntgSrCloudStrikes : '-'}
        <span style={{ display: 'block', fontSize: '0.8em', color: 'var(--text-dim)', marginTop: '1px' }}>(3 strikes your streak resets)</span>
      </span>
    )}
  </button>
  <button className="btn btn-daily btn-large" onClick={handleCreateNTGDaily} disabled={isCreating}>
    <span className="btn-main-text">👶 NAME THAT GUY <span style={{ color: '#ff3b30', fontWeight: 900 }}>JR.</span> DAILY CHALLENGE</span>
    <span className="btn-hint">
      {!currentUser && hasPlayedNTGToday() && getNTGDailyData()?.lastScore >= 1
      ? 'SIGN IN / SIGN UP TO KEEP YOUR STREAK'
      : hasPlayedNTGToday() && getNTGDailyData()?.lastScore >= 1
      ? (ntgCloudStreak > 0 ? `${ntgCloudStreak}-DAY STREAK` : 'PLAYED TODAY')
      : hasPlayedNTGToday()
      ? 'PLAYED TODAY. WARM UP AND TRY AGAIN TOMORROW'
      : ntgCloudStreak > 0
      ? `${ntgCloudStreak}-DAY STREAK`
      : '1 QUESTION · 10 SECONDS · EVERY DAY'}
    </span>
    {currentUser && (
      <span className="btn-hint" style={{ marginTop: '2px', textTransform: 'none' }}>
        stri<span style={{ color: '#ff3b30', fontWeight: 900, fontSize: '1.1em', verticalAlign: 'middle' }}>K</span>e: {ntgCloudStrikes >= 1 ? ntgCloudStrikes : '-'}
        <span style={{ display: 'block', fontSize: '0.8em', color: 'var(--text-dim)', marginTop: '1px' }}>(3 strikes your streak resets)</span>
      </span>
    )}
  </button>
  <button className="btn btn-daily btn-large" onClick={handleCreateDigitsDaily} disabled={isCreating} style={{ opacity: currentUser ? 1 : 0.6 }}>
    <span className="btn-main-text">#️⃣ DIGITS DAILY CHALLENGE</span>
    <span className="btn-hint">
      {!currentUser
      ? 'SIGN IN TO UNLOCK'
      : hasPlayedDigitsToday() && digitsCloudStreak > 0
      ? `${digitsCloudStreak}-DAY STREAK`
      : hasPlayedDigitsToday()
      ? 'PLAYED TODAY. WARM UP AND TRY AGAIN TOMORROW'
      : digitsCloudStreak > 0
      ? `${digitsCloudStreak}-DAY STREAK`
      : '1 QUESTION · 10 SECONDS · EVERY DAY'}
    </span>
    {currentUser && (
      <span className="btn-hint" style={{ marginTop: '2px', textTransform: 'none' }}>
        stri<span style={{ color: '#ff3b30', fontWeight: 900, fontSize: '1.1em', verticalAlign: 'middle' }}>K</span>e: {digitsCloudStrikes >= 1 ? digitsCloudStrikes : '-'}
        <span style={{ display: 'block', fontSize: '0.8em', color: 'var(--text-dim)', marginTop: '1px' }}>(3 strikes your streak resets)</span>
      </span>
    )}
  </button>
  <button className="btn btn-primary btn-large" onClick={() => {
    if (!currentUser) { setShowAuth(true); return; }
    setScreen(SCREENS.CREATE_MODE);
  }}>
    <span className="btn-main-text">⚔️ GUY OFF</span>
    <span className="btn-hint" style={{ fontSize: '10px', letterSpacing: '2px', opacity: 0.7 }}>HEAD TO HEAD</span>
    <span className="btn-hint">NFL, NBA, MISH MOSH · CREATE & SEND</span>
  </button>
</div>
          {dailyError && (
            <p style={{ color: '#ff6b6b', fontFamily: 'var(--font-body)', fontSize: '13px', textAlign: 'center', marginTop: '12px' }}>
              {dailyError}
            </p>
          )}
          <div className="home-rules">
            <button className="home-rules-toggle" style={{ marginBottom: showRules ? '2px' : '-8px' }} onClick={() => setShowRules(v => !v)}>
              HOW TO PLAY<span className="rules-chevron">{showRules ? '▴' : '▾'}</span>
            </button>
            {showRules && (<>
            <ul>
              <li><strong>DAILY:</strong> 5 questions, 7 seconds each - 3 out of 5 = <strong>you know your guy</strong>. Keep your streak.</li>
              <li><strong>NAME THAT GUY SR.:</strong> 1 guy, 3 guesses - the opener, then a college hint, then the résumé</li>
              <li><strong>NAME THAT GUY JR.:</strong> 1 question, 10 seconds - identify the <strong>guy</strong> to keep your streak</li>
              <li><strong>DIGITS:</strong> 1 question, 10 seconds - which guy wore (or never wore) the day's number. All sports count.</li>
              <li>
                <strong>GUY OFF:</strong> 5 questions, 7 seconds each - head to head - share your code with a friend
                <ul className="rules-sublist">
                  <li><strong>EASY:</strong> Multiple choice, 7 seconds per player</li>
                  <li><strong>HARD:</strong> Type the college, 7 seconds per player</li>
                  <li><strong>TIED?:</strong> Sudden death decides it</li>
                </ul>
              </li>
              <li><strong>STREAKS<span style={{ fontSize: '0.7em', verticalAlign: 'super' }}>*</span>:</strong> Play every day to keep your streak alive.</li>
            </ul>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: '10px', color: 'var(--text-dim)', marginTop: '8px', lineHeight: '1.4' }}>
              {'* Daily challenges: miss one full day and you\'re okay — miss two full days and your streak resets. LIDS: miss one full day and it resets. The daily resets at midnight Pacific (PT).'}
            </p>
            </>)}
          </div>
          <div style={{ textAlign: 'center', marginTop: '12px' }}>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: '11px', color: 'var(--text-dim)' }}>
              feedback? <a href="mailto:matt@knowyourguy.com" style={{ color: 'var(--gold)', textDecoration: 'none' }}>matt@knowyourguy.com</a>
{' · '}
<a href="/privacy" style={{ color: 'var(--text-dim)', textDecoration: 'none' }}>privacy policy</a>
            </p>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: '10px', color: 'var(--text-dim)', marginTop: '6px', opacity: 0.7 }}>
              Manufactured in Chicago, IL
            </p>
            {/* Flipped 2026-09-16: the Sr.-submit pitch moved up to the announcement slot
                and PocketBookie took this spot, under the feedback line. */}
            <div style={{ marginTop: '14px' }}>
              <PartnerBanner />
            </div>
          </div>
          {!currentUser && (
  <div className="leaderboard-cta" style={{ textAlign: 'center' }}>
  <button className="btn btn-outline" onClick={() => setShowAuth(true)} style={{ marginBottom: '12px' }}>SIGN IN / SIGN UP</button>
  <div style={{ background: 'var(--bg-navy)', border: '1px solid var(--border-dark)', borderRadius: 'var(--radius)', padding: '16px' }}>
    <p style={{ fontFamily: 'var(--font-body)', fontSize: '12px', color: 'var(--text-dim)', lineHeight: '1.8' }}>
      View the leaderboard<br />
      Track your daily streak and accuracy<br />
      Challenge friends head to head<br />
      Earn sweet, sweet pieces <span style={{ fontSize: '0.85em', opacity: 0.75 }}>(while supplies last)</span><br />
      The Handcrafted Daily (<a href="/sample-daily.html" target="_blank" rel="noreferrer" style={{ color: 'var(--gold)' }}>sample</a>) (opt out whenever)
    </p>
  </div>
</div>
)}
        </div>
        {showAuth && <AuthModal onAuth={handleAuth} onClose={() => setShowAuth(false)} />}
        {showUsernamePicker && currentUser && <UsernamePickerModal user={currentUser} onDone={(name) => { setCurrentUser(prev => ({ ...prev, username: name })); setShowUsernamePicker(false); }} />}
        {showSrSubmit && currentUser && <SubmitSrModal onClose={() => setShowSrSubmit(false)} />}
      </div>
    );
  }

if (screen === SCREENS.DAILY_LOCKED) {
  // lockedSport uses the storage keys; the nudge uses the home-screen keys.
  const LOCKED_TO_NUDGE_KEY = { nfl: 'nfl', nba: 'nba', mosh: 'mosh', ntg: 'jr', ntg_sr: 'sr', digits: 'digits' };
  // Closing line has THREE states, because "come back tomorrow" is wrong while
  // the nudge below is still offering NBA / Mish Mosh behind a sign-in.
  const notPlayed = (fn) => { try { return !fn(); } catch { return false; } };
  // Playable right now = unplayed AND not account-gated for this user.
  const lockedAnythingLeft = [
    hasPlayedNTGSrToday, hasPlayedNTGToday, hasPlayedToday,
    ...(currentUser ? [hasPlayedNBAToday, hasPlayedMoshToday, hasPlayedDigitsToday] : []),
  ].some(notPlayed);
  // Guests always have NBA + Mish Mosh sitting behind an account.
  const lockedGatedLeft = !currentUser;
  return (
    <div className="app" key={screen}>
      {renderTopNav()}
      <div className="results-screen">
        <div className="results-card">
          <p style={{ fontFamily: 'var(--font-body)', fontSize: '11px', color: 'var(--text-dim)', textAlign: 'center', marginBottom: '8px', opacity: 0.8 }}>
            feedback? <a href="mailto:matt@knowyourguy.com" style={{ color: 'var(--gold)', textDecoration: 'underline' }}>matt@knowyourguy.com</a>
          </p>
          {/* Compact cap matching the daily results refresh: this used to be a
              36px title in a 24px-padded banner, which pushed the "what else can
              I play" block off a phone screen entirely. */}
          <div className="daily-cap tie">
            <p className="daily-cap-eyebrow">&#10003; ALREADY PLAYED</p>
            <p className="daily-cap-headline">DAILY COMPLETE</p>
            <p className="daily-cap-score" style={{ letterSpacing: '1px' }}>
              {lockedAnythingLeft
                ? 'Plenty left below.'
                : lockedGatedLeft
                  ? 'Three more unlock with a free account.'
                  : 'That is all of them. Come back tomorrow.'}
            </p>
          </div>

          {/* The reason someone lands here is that they want to play. Point them
              at what is actually still open today (played games grey out). */}
          <NextGameNudge
            currentGame={LOCKED_TO_NUDGE_KEY[lockedSport] || 'nfl'}
            currentUser={currentUser}
            onPlayDaily={handlePlayDaily}
            onShowAuth={() => setShowAuth(true)}
          />

          <div className="results-banner tie" style={{ padding: '12px 14px', marginTop: '12px' }}>
           {currentUser ? (
  <>
    <div style={{ fontFamily: 'var(--font-heading)', fontSize: '12px', color: 'var(--gold)', letterSpacing: '2px' }}>
  SHARE WITH A FRIEND
</div>
    <button
      className="btn btn-primary"
      style={{ marginTop: '10px' }}
      onClick={() => {
  let text;
  if (lockedSport === 'ntg_sr') {
    // Tier from the STORED result (won + hintsUsed), not a generic line.
    const srData = getNTGSrDailyData();
    text = ntgSrShareText((srData?.lastScore ?? 0) >= 1, srData?.hintsUsed ?? 2);
  } else if (lockedSport === 'ntg') {
    // Tier from the STORED result, not a generic line (the old bug lived here).
    text = ntgJrShareText((getNTGDailyData()?.lastScore ?? 0) >= 1);
  } else if (lockedSport === 'digits') {
    // Same copy the results screen shares; outcome and streak from the STORED
    // result. Without this branch Digits fell through to the NFL text.
    const dData = getDigitsDailyData();
    const dStreak = dData?.streak ?? 0;
    text = (dData?.lastScore ?? 0) >= 1
      ? `Called today's Digits #️⃣ on KYG. ${dStreak > 0 ? `${dStreak}-day streak. ` : ''}Which guy wore it? knowyourguy.com`
      : `Today's Digits number got me #️⃣💀 Bet you don't know which guy wore it. knowyourguy.com`;
  } else {
    const score = lockedSport === 'nba'
    ? getNBADailyData()?.lastScore ?? null
    : lockedSport === 'mosh'
    ? getMoshDailyData()?.lastScore ?? null
    : getDailyData()?.lastScore ?? null;
    const emoji = lockedSport === 'nba' ? '🏀' : lockedSport === 'mosh' ? '🍲' : '🏈';
    const scoreText = score !== null ? ` I scored ${score}/5.` : '';
    const sport = lockedSport === 'nba' ? 'NBA' : lockedSport === 'mosh' ? 'Mish Mosh' : 'NFL';
    text = `I just played today's Know Your Guy ${sport} daily challenge ${emoji}${scoreText}\nThink you can beat my score? knowyourguy.com`;
  }
        if (navigator.share) {
          navigator.share({ title: 'Know Your Guy', text });
        } else {
          navigator.clipboard.writeText(text).then(() => alert('Copied to clipboard.'));
        }
      }}
    >
      SHARE TODAY'S CHALLENGE
    </button>
  </>

            ) : (
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontFamily: 'var(--font-heading)', fontSize: '12px', color: 'var(--gold)', letterSpacing: '2px', marginBottom: '6px' }}>
                  SIGN UP TO SAVE YOUR STREAK
                </p>
                <p style={{ fontFamily: 'var(--font-body)', fontSize: '11px', color: 'var(--text-dim)', marginBottom: '10px' }}>
                  Create a free account and challenge friends head to head.
                </p>
                <button className="btn btn-primary" style={{ marginTop: '0' }} onClick={() => setShowAuth(true)}>
                  SIGN UP...
                </button>
              </div>
            )}
          </div>
          <div className="results-actions">
            <button className="btn btn-secondary btn-large" onClick={handleGoHome}>
              <span className="btn-main-text">BACK TO HOME</span>
            </button>
          </div>
        </div>
      </div>
      {showAuth && <AuthModal onAuth={handleAuth} onClose={() => setShowAuth(false)} />}
        {showUsernamePicker && currentUser && <UsernamePickerModal user={currentUser} onDone={(name) => { setCurrentUser(prev => ({ ...prev, username: name })); setShowUsernamePicker(false); }} />}
    </div>
  );
}
if (screen === SCREENS.EXPIRED) {
  return (
    <div className="app" key={screen}>
      {renderTopNav()}
      <div className="results-screen">
        <div className="results-card">
          <div className="results-banner loser">
            <div style={{ fontSize: '36px', marginBottom: '8px' }}>⏰</div>
            <div className="results-title">GAME EXPIRED</div>
            <div className="results-message">This invite link has expired.<br />Games expire after 48 hours.</div>
          </div>
          <div className="results-actions">
            <button className="btn btn-primary btn-large" onClick={() => {
              if (!currentUser) { setShowAuth(true); return; }
              setScreen(SCREENS.CREATE_MODE);
            }}>
              <span className="btn-main-text">CREATE NEW GAME</span>
            </button>
            <button className="btn btn-secondary btn-large" onClick={handleGoHome}>
              <span className="btn-main-text">BACK TO HOME</span>
            </button>
          </div>
        </div>
      </div>
      {showAuth && <AuthModal onAuth={handleAuth} onClose={() => setShowAuth(false)} />}
        {showUsernamePicker && currentUser && <UsernamePickerModal user={currentUser} onDone={(name) => { setCurrentUser(prev => ({ ...prev, username: name })); setShowUsernamePicker(false); }} />}
    </div>
  );
}
if (screen === SCREENS.SPORT_SELECT) {
  return (
    <div className="app" key={screen}>
      {renderTopNav()}
      <div className="mode-select-screen">
        <button className="btn btn-back" onClick={() => setScreen(SCREENS.CREATE_MODE)}>&larr; BACK</button>
        <h2 className="mode-title">GUY OFF</h2>
        <div className="title-stripe small" />
        <p className="mode-subtitle">PICK YOUR SPORT</p>
        <div className="mode-cards">
          <button className="mode-card easy" onClick={() => { setH2hSport('nfl'); setScreen(SCREENS.MODE_SELECT); }}>
            <div className="mode-card-header">
              <span className="mode-card-icon">🏈</span>
              <h3>NFL</h3>
            </div>
            <ul><li>Match NFL players to their college</li></ul>
            <div className="mode-card-arrow">&rarr;</div>
          </button>
<button className="mode-card easy" onClick={() => { setH2hSport('nba'); setScreen(SCREENS.MODE_SELECT); }}>
            <div className="mode-card-header">
              <span className="mode-card-icon">🏀</span>
              <h3>NBA</h3>
            </div>
            <ul><li>Match NBA players to their college</li></ul>
            <div className="mode-card-arrow">&rarr;</div>
          </button>
          <button className="mode-card" style={{ borderLeftColor: '#c9a84c' }} onClick={() => { setH2hSport('mosh'); setScreen(SCREENS.MODE_SELECT); }}>
            <div className="mode-card-header">
              <span className="mode-card-icon">🍲</span>
              <h3>MISH MOSH</h3>
            </div>
            <ul><li>Match notable alumni to their college</li></ul>
            <div className="mode-card-arrow">&rarr;</div>
          </button>
        </div>
      </div>
      {showAuth && <AuthModal onAuth={handleAuth} onClose={() => setShowAuth(false)} />}
        {showUsernamePicker && currentUser && <UsernamePickerModal user={currentUser} onDone={(name) => { setCurrentUser(prev => ({ ...prev, username: name })); setShowUsernamePicker(false); }} />}
    </div>
  );
}  
if (screen === SCREENS.CREATE_MODE) {
    return (
      <div className="app" key={screen}>
        {renderTopNav()}
        {currentUser && showProfile && (
  <div ref={profileRef}>
    <Profile
      user={currentUser}
      authReady={authReady}
      onUsernameChange={(name) => setCurrentUser(prev => ({ ...prev, username: name }))}
    />
  </div>
)}
{currentUser && showLeaderboard && (
  <div ref={leaderboardRef}>
    <Leaderboard currentUser={currentUser} authReady={authReady} />
  </div>
)}
        <div className="mode-select-screen">
          <button className="btn btn-back" onClick={() => setScreen(SCREENS.HOME)}>&larr; BACK</button>
          <h2 className="mode-title">GUY OFF</h2>
          <div className="title-stripe small" />
          <p className="mode-subtitle">PLAY A FRIEND &middot; SAME QUESTIONS</p>
          <div className="mode-cards">
            <button className="btn btn-primary btn-large" onClick={() => {
              if (!currentUser) { setShowAuth(true); return; }
              setScreen(SCREENS.SPORT_SELECT);
            }}>
              <span className="btn-main-text">CREATE GAME</span>
              <span className="btn-hint">PICK YOUR BATTLE</span>
            </button>
          </div>
          {currentUser && renderGIP()}
        </div>
        {showAuth && <AuthModal onAuth={handleAuth} onClose={() => setShowAuth(false)} />}
        {showUsernamePicker && currentUser && <UsernamePickerModal user={currentUser} onDone={(name) => { setCurrentUser(prev => ({ ...prev, username: name })); setShowUsernamePicker(false); }} />}
      </div>
    );
  }

  if (screen === SCREENS.MODE_SELECT) {
    return (
      <div className="app" key={screen}>
        {renderTopNav()}
        <div className="mode-select-screen">
          <button className="btn btn-back" onClick={() => setScreen(SCREENS.SPORT_SELECT)}>&larr; BACK</button>
          <h2 className="mode-title">SELECT MODE</h2>
          <div className="title-stripe small" />
          <p className="mode-subtitle">BOTH PLAYERS WILL PLAY THE SAME MODE</p>
          <div className="mode-cards">
            <button className="mode-card easy" onClick={() => handleCreateGame('easy')} disabled={isCreating}>
              <div className="mode-card-header">
                <span className="mode-card-icon">&#9733;</span>
                <h3>EASY</h3>
              </div>
              <ul>
                <li>Multiple choice </li>
                <li>7 seconds per question</li>
                <li>Click to answer</li>
              </ul>
              <div className="mode-card-arrow">&rarr;</div>
            </button>
            <button className="mode-card hard" onClick={() => handleCreateGame('hard')} disabled={isCreating}>
              <div className="mode-card-header">
                <span className="mode-card-icon">&#9889;</span>
                <h3>HARD</h3>
              </div>
              <ul>
                <li>Type the college name</li>
                <li>7 seconds per question</li>
                <li>Fuzzy matching enabled</li>
              </ul>
              <div className="mode-card-arrow">&rarr;</div>
            </button>
          </div>
          {createError && (
            <p style={{ color: '#ff6b6b', fontFamily: 'var(--font-body)', fontSize: '13px', textAlign: 'center', marginTop: '12px' }}>
              {createError}
            </p>
          )}
        </div>
        {showAuth && <AuthModal onAuth={handleAuth} onClose={() => setShowAuth(false)} />}
        {showUsernamePicker && currentUser && <UsernamePickerModal user={currentUser} onDone={(name) => { setCurrentUser(prev => ({ ...prev, username: name })); setShowUsernamePicker(false); }} />}
      </div>
    );
  }

  if ((screen === SCREENS.NTG_SR_BEGIN || screen === SCREENS.NTG_SR_PLAYING) && ntgSrPuzzle) {
  return (
    <div className="app" key="ntg-sr">
      <GameX
        puzzleRow={ntgSrPuzzle}
        started={screen === SCREENS.NTG_SR_PLAYING}
        onBegin={() => setScreen(SCREENS.NTG_SR_PLAYING)}
        currentUser={currentUser}
        claimedStreak={claimedStreaks?.ntg_sr}
        onGoHome={handleGoHome}
        onShowAuth={() => setShowAuth(true)}
        onPlayDaily={handlePlayDaily}
        onDailyComplete={(newStreak, ironmanStreak, newStrikes) => {
          if (newStreak !== undefined) setNtgSrCloudStreak(newStreak);
          if (newStrikes !== undefined) setNtgSrCloudStrikes(newStrikes);
          if (ironmanStreak !== undefined) setIronmanCloudStreak(ironmanStreak);
        }}
      />
      {showAuth && <AuthModal onAuth={handleAuth} onClose={() => setShowAuth(false)} />}
      {showUsernamePicker && currentUser && <UsernamePickerModal user={currentUser} onDone={(name) => { setCurrentUser(prev => ({ ...prev, username: name })); setShowUsernamePicker(false); }} />}
    </div>
  );
}

  if ((screen === SCREENS.DIGITS_BEGIN || screen === SCREENS.DIGITS_PLAYING) && digitsQuestion) {
  return (
    <div className="app" key="digits">
      <DigitsGame
        question={digitsQuestion}
        started={screen === SCREENS.DIGITS_PLAYING}
        onBegin={() => setScreen(SCREENS.DIGITS_PLAYING)}
        currentUser={currentUser}
        claimedStreaks={claimedStreaks}
        onGoHome={handleGoHome}
        onShowAuth={() => setShowAuth(true)}
        onPlayDaily={handlePlayDaily}
        onDailyComplete={(sport, newStreak, ironmanStreak, newStrikes) => {
          if (sport === 'digits' && newStreak !== undefined) setDigitsCloudStreak(newStreak);
          if (sport === 'digits' && newStrikes !== undefined) setDigitsCloudStrikes(newStrikes);
          if (ironmanStreak !== undefined) setIronmanCloudStreak(ironmanStreak);
        }}
      />
      {showAuth && <AuthModal onAuth={handleAuth} onClose={() => setShowAuth(false)} />}
      {showUsernamePicker && currentUser && <UsernamePickerModal user={currentUser} onDone={(name) => { setCurrentUser(prev => ({ ...prev, username: name })); setShowUsernamePicker(false); }} />}
    </div>
  );
}

  if ((screen === SCREENS.NTG_BEGIN || screen === SCREENS.NTG_PLAYING) && ntgQuestion) {
  return (
    <div className="app" key="ntg">
      <NameThatGuyGame
        question={ntgQuestion}
        started={screen === SCREENS.NTG_PLAYING}
        onBegin={() => setScreen(SCREENS.NTG_PLAYING)}
        currentUser={currentUser}
        claimedStreaks={claimedStreaks}
        onGoHome={handleGoHome}
        onShowAuth={() => setShowAuth(true)}
        onPlayDaily={handlePlayDaily}
        onDailyComplete={(sport, newStreak, ironmanStreak, newStrikes) => {
          if (sport === 'ntg' && newStreak !== undefined) setNtgCloudStreak(newStreak);
          if (sport === 'ntg' && newStrikes !== undefined) setNtgCloudStrikes(newStrikes);
          if (ironmanStreak !== undefined) setIronmanCloudStreak(ironmanStreak);
        }}
      />
      {showAuth && <AuthModal onAuth={handleAuth} onClose={() => setShowAuth(false)} />}
      {showUsernamePicker && currentUser && <UsernamePickerModal user={currentUser} onDone={(name) => { setCurrentUser(prev => ({ ...prev, username: name })); setShowUsernamePicker(false); }} />}
    </div>
  );
}

  if (screen === SCREENS.NFL_BEGIN && gameState) {
  return (
    <div className="app" key="nfl-begin">
      <div className="round-break">
        <button className="btn-home-corner" onClick={handleGoHome}>HOME</button>
        <div className="round-break-content">
          <div className="round-break-icon">🏈</div>
          <div className="round-break-badge">DAILY</div>
          <h2 className="round-break-title">CHALLENGE</h2>
          <p className="round-break-desc">5 questions, 7 seconds each.<br />Get 3 or more correct to keep your streak.</p>
          <div className="round-break-mode">EASY &bull; MC</div>
          <button className="btn btn-begin" onClick={() => setScreen(SCREENS.PLAYING)}>BEGIN</button>
          <div className="round-break-brand">knowyourguy.com</div>
        </div>
      </div>
      {showAuth && <AuthModal onAuth={handleAuth} onClose={() => setShowAuth(false)} />}
    </div>
  );
}

  if (screen === SCREENS.PLAYING && gameState) {
  return (
    <div className="app" key={screen}>

      <GameBoard
        key={gameState?.gbKey ?? gameState?.matchId ?? 'gb'}
        initialGameState={gameState}
        onGameEnd={handleGoHome}
        onGoHome={handleGoHome}
        onRematch={handleRematch}
        currentUser={currentUser}
        onShowAuth={() => setShowAuth(true)}
        onPlayDaily={handlePlayDaily}
        claimedStreaks={claimedStreaks}
        onDailyComplete={(sport, newStreak, ironmanStreak) => {
          if (sport === 'nfl') setCloudStreak(newStreak);
          else if (sport === 'nba') setNBACloudStreak(newStreak);
          else if (sport === 'mosh') setMoshCloudStreak(newStreak);
          if (ironmanStreak !== undefined) setIronmanCloudStreak(ironmanStreak);
        }}
      />
      {showAuth && <AuthModal onAuth={handleAuth} onClose={() => setShowAuth(false)} />}
        {showUsernamePicker && currentUser && <UsernamePickerModal user={currentUser} onDone={(name) => { setCurrentUser(prev => ({ ...prev, username: name })); setShowUsernamePicker(false); }} />}
    </div>
  );
}
}

export default function AppWrapper() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/join/:matchId" element={<App joinMode />} />
        <Route path="/ntg" element={<App />} />
        <Route path="/nfl" element={<App />} />
        <Route path="/sr" element={<App />} />
        <Route path="/*" element={<App />} />
      </Routes>
      <InstallPrompt />
      <Analytics />
    </BrowserRouter>
  );
}
